// DESIGN_AUDIT Phase 11C: hardcoded colors replaced with CSS vars
// DESIGN_AUDIT Phase 11A fixes applied:
//   FIX 1: Replaced 3 occurrences of var(--bg-card) with var(--bg-elevated) — --bg-card was undefined
// DESIGN_AUDIT Phase 11B: all modals converted to ReactDOM.createPortal
import ReactDOM from 'react-dom';
import { useState, useMemo, useRef, useEffect, type MouseEvent as ReactMouseEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Search, Users, Phone, Mail, Globe, Star,
  X, Clock, Hash, Pencil, Trash2, Building2, Upload,
  ChevronDown, ChevronRight, Settings2, ImageOff, Smartphone,
  Lock, AlertTriangle, Calendar, Sparkles, Share2,
  FileText, Eye, DollarSign, Paperclip, MapPin,
  MessageSquare, ArrowRight, PlusSquare, Tags, GripVertical,
} from 'lucide-react';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import apiClient, { api } from '../api/client.ts';
import { getImageSrc } from '../utils/imageUrl.ts';
import { Vendor, ServiceCategory, VendorContact } from '../types/index.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { PdfViewerButton } from '../components/PdfViewer.tsx';
import DarkSelect from '../components/DarkSelect.tsx';
import DatePicker from '../components/DatePicker.tsx';
import Toast, { useToast } from '../components/Toast';
import { spinIconOut } from '../utils/iconAnimations.ts';
import './Vendors.css';
import '../styles/buttonAnimations.css';

// ─── Pill label styles ────────────────────────────────────────────────────────
const CONTACT_PILL = {
  background: 'rgba(16,185,129,0.15)', border: '1px solid #10b981', color: '#6ee7b7',
  fontSize: 12, fontWeight: 500, padding: '2px 8px', borderRadius: 99, display: 'inline-block',
} as const;
const CONTRACT_PILL = {
  background: 'rgba(245,158,11,0.15)', border: '1px solid #f59e0b', color: '#fcd34d',
  fontSize: 12, fontWeight: 500, padding: '2px 8px', borderRadius: 99, display: 'inline-block',
} as const;

function getVendorProxyUrl(fileUrl: string, fileName: string): string {
  const token = localStorage.getItem('accessToken');
  const base = import.meta.env.VITE_API_URL;
  return `${base}/vendors/file-proxy?url=${encodeURIComponent(fileUrl)}&filename=${encodeURIComponent(fileName || 'document.pdf')}&token=${encodeURIComponent(token || '')}`;
}

function openVendorFile(fileUrl: string, fileName: string) {
  window.open(getVendorProxyUrl(fileUrl, fileName), '_blank', 'noopener,noreferrer');
}

// ─── Types ────────────────────────────────────────────────────────────────────

type AiRanking = { vendor_name: string; rating: number; strengths: string[]; concerns: string[]; recommendation: string };
type AiCompareResult = { rankings: AiRanking[]; summary: string; vendorCount?: number; batchCount?: number; comparisonId?: number };
type SavedComparison = { id: number; title: string; vendor_count: number; batch_count: number; summary: string | null; created_at: string };

type SpendEntry = {
  id: number;
  vendor_id: number;
  facility_id: number;
  service_date: string;
  amount: string;
  description: string | null;
  invoice_number: string | null;
  created_by_name: string | null;
  created_at: string;
};
type SpendTotals = { data: SpendEntry[]; ytd_total: number };

type VendorDocument = {
  id: number;
  vendor_id: number;
  facility_id: number;
  doc_type: string;
  file_url: string;
  file_name: string;
  expiry_date: string | null;
  notes: string | null;
  days_until_expiry: number | null;
  uploaded_by: number | null;
  created_at: string;
  updated_at: string;
};

const DOC_TYPES = ['COI', 'W9', 'License', 'Contract', 'Background Check', 'Other'] as const;

type VendorRating = {
  id: number;
  vendor_id: number;
  facility_id: number;
  rated_by: number | null;
  rated_by_name: string | null;
  service_date: string;
  response_time: number;
  work_quality: number;
  communication: number;
  pricing: number;
  reliability: number;
  notes: string | null;
  created_at: string;
};

type RatingAverages = {
  avg_response_time: string | null;
  avg_work_quality: string | null;
  avg_communication: string | null;
  avg_pricing: string | null;
  avg_reliability: string | null;
  avg_overall: string | null;
  total_ratings: string;
};

type RatingsData = { ratings: VendorRating[]; averages: RatingAverages };

// Vendor extended with rating fields (added by migration v99)
type VendorWithRating = Vendor & { avg_rating?: string | null; rating_count?: number | null };

const RATING_CRITERIA = [
  { key: 'response_time' as const, label: 'Response Time', icon: '🕐' },
  { key: 'work_quality' as const, label: 'Work Quality', icon: '🔧' },
  { key: 'communication' as const, label: 'Communication', icon: '💬' },
  { key: 'pricing' as const, label: 'Pricing', icon: '💰' },
  { key: 'reliability' as const, label: 'Reliability', icon: '✅' },
] as const;

type VendorForm = {
  name: string;
  is_main_vendor: boolean;
  service_categories: string[];
  vendor_phone: string;
  vendor_email: string;
  account_number: string;
  website: string;
  hours_of_operation: string;
  is_24_hour_service: boolean;
  notes: string;
  contract_start_date: string;
  contract_expiry_date: string;
  contract_notes: string;
  logo_url: string | null;
};

const EMPTY_FORM: VendorForm = {
  name: '',
  is_main_vendor: false,
  service_categories: [],
  vendor_phone: '',
  vendor_email: '',
  account_number: '',
  website: '',
  hours_of_operation: '',
  is_24_hour_service: false,
  notes: '',
  contract_start_date: '',
  contract_expiry_date: '',
  contract_notes: '',
  logo_url: null,
};

function formToPayload(f: VendorForm) {
  return {
    name: f.name,
    is_main_vendor: f.is_main_vendor,
    service_categories: f.service_categories,
    vendor_phone: f.vendor_phone || null,
    vendor_email: f.vendor_email || null,
    account_number: f.account_number || null,
    website: f.website || null,
    hours_of_operation: f.hours_of_operation || null,
    is_24_hour_service: f.is_24_hour_service,
    notes: f.notes || null,
    contract_start_date: f.contract_start_date || null,
    contract_expiry_date: f.contract_expiry_date || null,
    contract_notes: f.contract_notes || null,
    logo_url: f.logo_url || null,
  };
}

function vendorToForm(v: Vendor): VendorForm {
  return {
    name: v.name,
    is_main_vendor: v.is_main_vendor,
    service_categories: v.service_categories,
    vendor_phone: v.vendor_phone ?? '',
    vendor_email: v.vendor_email ?? '',
    account_number: v.account_number ?? '',
    website: v.website ?? '',
    hours_of_operation: v.hours_of_operation ?? '',
    is_24_hour_service: v.is_24_hour_service,
    notes: v.notes ?? '',
    contract_start_date: v.contract_start_date ? v.contract_start_date.split('T')[0] : '',
    contract_expiry_date: v.contract_expiry_date ? v.contract_expiry_date.split('T')[0] : '',
    contract_notes: v.contract_notes ?? '',
    logo_url: v.logo_url ?? null,
  };
}

// Display formatter for the same two DATE columns (contract_start_date/contract_expiry_date) --
// same safe y/m/d local-constructor pattern as contractExpiryStatus below, used instead of the
// bare `new Date(val).toLocaleDateString()` this file's Contract Info panel used to call directly.
function fmtContractDate(val: string | null | undefined): string | null {
  if (!val) return null;
  const s = String(val).slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// ─── Contract expiry helpers ──────────────────────────────────────────────────
// vendors.contract_expiry_date is a plain DATE column -- same bug class as the Orders "Expected
// Delivery Date" off-by-one. `new Date(expiryDate)` on a UTC-midnight timestamp (what
// server/src/db.ts used to return for every DATE column, and what a TIMESTAMPTZ literally at UTC
// midnight still looks like) is a UTC instant; for anyone west of UTC that instant falls on the
// PREVIOUS local day, and the .setHours(0,0,0,0) right after it "snaps to midnight" of that
// already-shifted-back day rather than correcting it -- so a contract expiring 9/30 could read as
// expired/soon a day early, not just display wrong. Fixed the same way as every other DATE field
// in this codebase (Reports.tsx/Inventory.tsx/Tasks.tsx/Projects.tsx): slice to 'YYYY-MM-DD' and
// build the Date via the LOCAL y/m/d constructor, which interprets the calendar-date components
// directly with no UTC/local conversion step for anything to shift.
function contractExpiryStatus(expiryDate: string | null): 'ok' | 'soon' | 'expired' | null {
  if (!expiryDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const s = String(expiryDate).slice(0, 10);
  const [ey, em, ed] = s.split('-').map(Number);
  if (!ey || !em || !ed) return null;
  const expiry = new Date(ey, em - 1, ed);
  const daysLeft = Math.floor((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (daysLeft < 0) return 'expired';
  if (daysLeft <= 30) return 'soon';
  return 'ok';
}

function safeWebsiteUrl(url: string | null): string | null {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
}

const CountUp = ({ value, duration = 1600 }: { value: number; duration?: number }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let raf: number;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 2);
      setDisplay(Math.round(value * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <>{display}</>;
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function VendorsPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isPro = user?.orgPlanTier === 'pro' || user?.orgPlanTier === 'enterprise' || user?.role === 'admin';
  const isEnterprise = user?.orgPlanTier === 'enterprise' || user?.role === 'admin';
  const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'org_admin' || user?.role === 'admin';
  const [search, setSearch] = useState('');
  const [activeCategories, setActiveCategories] = useState<string[]>([]);
  const [filterExpanded, setFilterExpanded] = useState(false);
  const [vendorSort, setVendorSort] = useState<'name' | 'highest_rated' | 'lowest_rated' | 'most_reviewed'>('name');
  const [showModal, setShowModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [form, setForm] = useState<VendorForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [modalExtracting, setModalExtracting] = useState(false);
  const [modalExtractError, setModalExtractError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Vendor | null>(null);
  const [confirmDenyShared, setConfirmDenyShared] = useState<Vendor | null>(null);
  const [slideVendor, setSlideVendor] = useState<Vendor | null>(null);
  const [showManageServices, setShowManageServices] = useState(false);
  const [newVendorContacts, setNewVendorContacts] = useState<ContactForm[]>([]);
  const [shareVendor, setShareVendor] = useState<Vendor | null>(null);
  const [shareTargets, setShareTargets] = useState<number[]>([]);
  const [emailVendor, setEmailVendor] = useState<Vendor | null>(null);
  const [emailForm, setEmailForm] = useState({ to: '', subject: '', message: '' });
  const { toast: emailToast, showToast: showEmailToast } = useToast();
  const [emailSuggestions, setEmailSuggestions] = useState<string[]>([]);
  const [showEmailSuggestions, setShowEmailSuggestions] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiResult, setAiResult] = useState<AiCompareResult | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiComparisonId, setAiComparisonId] = useState<number | null>(null);
  const [aiModalTab, setAiModalTab] = useState<'new' | 'history'>('new');
  const [showEnterpriseHint, setShowEnterpriseHint] = useState(false);
  const [highlightVendorId, setHighlightVendorId] = useState<number | null>(null);
  const didOpenVendorFromUrl = useRef(false);
  const mouseDownTarget = useRef<EventTarget | null>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const suggestionTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ['vendors', search],
    queryFn: () =>
      api.get<Vendor[]>('/vendors', {
        search: search || undefined,
      }),
  });

  const { data: catRes } = useQuery({
    queryKey: ['service-categories'],
    queryFn: () => api.get<ServiceCategory[]>('/vendors/service-categories'),
  });

  const { data: expiringRes } = useQuery({
    queryKey: ['vendors-expiring'],
    queryFn: () => api.get<{ id: number; name: string; contract_expiry_date: string }[]>('/vendors/expiring'),
    enabled: isPro,
  });
  const expiringVendors = (expiringRes?.data ?? []) as { id: number; name: string; contract_expiry_date: string }[];

  const { data: sisterRes } = useQuery({
    queryKey: ['sister-facilities'],
    queryFn: () => api.get<{ id: number; name: string }[]>('/vendors/sister-facilities'),
    enabled: isPro,
    staleTime: 60000,
  });
  const sisterFacilities = (sisterRes?.data ?? []) as { id: number; name: string }[];

  const { data: comparisonsRes } = useQuery({
    queryKey: ['vendor-ai-comparisons'],
    queryFn: () => api.get<SavedComparison[]>('/vendors/ai-comparisons'),
    enabled: isEnterprise && aiModalTab === 'history',
    staleTime: 30000,
  });
  const savedComparisons = (comparisonsRes?.data ?? []) as SavedComparison[];

  const { data: expiringDocsRes } = useQuery({
    queryKey: ['vendor-expiring-docs'],
    queryFn: () => api.get<{ vendor_id: number }[]>('/vendors/expiring-documents'),
    enabled: isPro,
    staleTime: 60000,
  });
  const expiringDocVendorIds = useMemo(() => {
    const rows = (expiringDocsRes?.data ?? []) as { vendor_id: number }[];
    return new Set(rows.map((d) => d.vendor_id));
  }, [expiringDocsRes]);

  const [pendingShared, setPendingShared] = useState<any[]>([]);
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const facilityId = localStorage.getItem('selectedFacilityId') || '';
    fetch(`${import.meta.env.VITE_API_URL || '/api'}/vendors/pending-shared`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'x-facility-id': facilityId,
      }
    })
      .then(r => r.json())
      .then(data => {
        console.log('[pending-shared] fetch result:', data);
        const rows = data?.data ?? (Array.isArray(data) ? data : []);
        console.log('[pending-shared] rows extracted:', rows.length);
        setPendingShared(rows);
      })
      .catch(err => console.error('[pending-shared] fetch error:', err));
  }, []);

  const serviceCategories: ServiceCategory[] = catRes?.data ?? [];
  const catMap = useMemo(() => {
    const m = new Map<string, ServiceCategory>();
    serviceCategories.forEach((c) => m.set(c.name, c));
    return m;
  }, [serviceCategories]);

  const vendors = data?.data ?? [];

  // Open vendor slide panel from URL param (global search navigation)
  useEffect(() => {
    if (didOpenVendorFromUrl.current || vendors.length === 0) return;
    const sp = new URLSearchParams(window.location.search);
    const vendorIdParam = sp.get('vendorId');
    if (!vendorIdParam) { didOpenVendorFromUrl.current = true; return; }
    const vendor = vendors.find(v => v.id === Number(vendorIdParam));
    if (vendor) {
      setSlideVendor(vendor);
      setHighlightVendorId(vendor.id);
      didOpenVendorFromUrl.current = true;
      setTimeout(() => {
        const el = document.getElementById(`vendor-card-${vendor.id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      setTimeout(() => setHighlightVendorId(null), 3000);
    }
  }, [vendors]);

  const displayVendors = useMemo(() => {
    let list = (activeCategories.length === 0
      ? vendors
      : vendors.filter((v) => activeCategories.some((cat) => v.service_categories.includes(cat))))
      .filter((v) => v.shared_status !== 'pending');

    // Apply sort
    if (vendorSort === 'highest_rated') {
      list = [...list].sort((a, b) => {
        const ra = parseFloat((a as VendorWithRating).avg_rating ?? '') || 0;
        const rb = parseFloat((b as VendorWithRating).avg_rating ?? '') || 0;
        if (ra === 0 && rb === 0) return a.name.localeCompare(b.name);
        if (ra === 0) return 1;
        if (rb === 0) return -1;
        return rb - ra;
      });
    } else if (vendorSort === 'lowest_rated') {
      list = [...list].sort((a, b) => {
        const ra = parseFloat((a as VendorWithRating).avg_rating ?? '') || 0;
        const rb = parseFloat((b as VendorWithRating).avg_rating ?? '') || 0;
        if (ra === 0 && rb === 0) return a.name.localeCompare(b.name);
        if (ra === 0) return 1;
        if (rb === 0) return -1;
        return ra - rb;
      });
    } else if (vendorSort === 'most_reviewed') {
      list = [...list].sort((a, b) => {
        const ca = ((a as VendorWithRating).rating_count ?? 0);
        const cb = ((b as VendorWithRating).rating_count ?? 0);
        return cb - ca;
      });
    } else {
      // Default name sort — but preferred vendors still top for Pro
      if (isPro) {
        list = [...list].sort((a, b) => {
          if (a.is_preferred && !b.is_preferred) return -1;
          if (!a.is_preferred && b.is_preferred) return 1;
          return 0;
        });
      }
    }

    return list;
  }, [vendors, activeCategories, isPro, vendorSort]);

  function toggleCategory(cat: string) {
    setActiveCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  }

  const categoryTabs = useMemo(() => {
    const names = new Set<string>(serviceCategories.map((c) => c.name));
    vendors.forEach((v) => v.service_categories.forEach((c) => names.add(c)));
    return Array.from(names).sort();
  }, [vendors, serviceCategories]);

  const mainVendorCount = vendors.filter((v) => v.is_main_vendor).length;
  const available247Count = vendors.filter((v) => v.is_24_hour_service).length;
  const activeCategoryCount = new Set(vendors.flatMap((v) => v.service_categories)).size;

  // ─── Mutations ────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (payload: ReturnType<typeof formToPayload>) =>
      api.post<Vendor>('/vendors', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vendors'] });
      closeModal();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ReturnType<typeof formToPayload> }) =>
      api.put<Vendor>(`/vendors/${id}`, payload),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['vendors'] });
      if (slideVendor && res.data) setSlideVendor(res.data);
      closeModal();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/vendors/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vendors'] });
      setConfirmDelete(null);
      setSlideVendor(null);
    },
  });

  const preferredMutation = useMutation({
    mutationFn: (id: number) => api.patch<Vendor>(`/vendors/${id}/preferred`, {}),
    onMutate: async (id: number) => {
      // Optimistic update
      await qc.cancelQueries({ queryKey: ['vendors'] });
      const prev = qc.getQueryData<{ data: Vendor[] }>(['vendors', search]);
      if (prev?.data) {
        qc.setQueryData(['vendors', search], {
          ...prev,
          data: prev.data.map((v) => v.id === id ? { ...v, is_preferred: !v.is_preferred } : v),
        });
      }
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(['vendors', search], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['vendors'] });
      qc.invalidateQueries({ queryKey: ['vendors-expiring'] });
    },
  });

  const shareMutation = useMutation({
    mutationFn: ({ id, targetFacilityIds }: { id: number; targetFacilityIds: number[] }) =>
      api.post<{ shared: number }>(`/vendors/${id}/share`, { targetFacilityIds }),
    onSuccess: () => {
      setShareVendor(null);
      setShareTargets([]);
    },
  });

  const approveMutation = useMutation({
    mutationFn: (id: number) => api.patch(`/vendors/${id}/approve-shared`, {}),
    onSuccess: (_data, id) => {
      setPendingShared((prev) => prev.filter((v) => v.id !== id));
      qc.invalidateQueries({ queryKey: ['vendors'] });
      showEmailToast('Vendor approved and added to your list');
    },
    onError: () => showEmailToast('Failed to approve vendor', true),
  });

  const denyMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/vendors/${id}/deny-shared`),
    onSuccess: (_data, id) => {
      setPendingShared((prev) => prev.filter((v) => v.id !== id));
      showEmailToast('Vendor denied and removed.');
    },
    onError: () => showEmailToast('Failed to deny vendor', true),
  });

  // ─── Handlers ────────────────────────────────────────────────────────────

  function openAdd() {
    setEditingVendor(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  }

  function openEdit(vendor: Vendor) {
    setEditingVendor(vendor);
    setForm(vendorToForm(vendor));
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditingVendor(null);
    setForm(EMPTY_FORM);
    setSaving(false);
    setNewVendorContacts([]);
    setModalExtractError(null);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = formToPayload(form);
      if (editingVendor) {
        await updateMutation.mutateAsync({ id: editingVendor.id, payload });
      } else {
        const res = await api.post<Vendor>('/vendors', payload);
        if (res.data && isPro && newVendorContacts.length > 0) {
          await Promise.all(
            newVendorContacts.map((c) => api.post(`/vendors/${res.data!.id}/contacts`, c))
          );
        }
        qc.invalidateQueries({ queryKey: ['vendors'] });
        closeModal();
      }
    } finally {
      setSaving(false);
    }
  }

  function setField<K extends keyof VendorForm>(key: K, value: VendorForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function toggleServiceCategory(cat: string) {
    setForm((f) => ({
      ...f,
      service_categories: f.service_categories.includes(cat)
        ? f.service_categories.filter((c) => c !== cat)
        : [...f.service_categories, cat],
    }));
  }

  async function extractForModal() {
    if (!form.name.trim()) {
      setModalExtractError('Enter a vendor name first so Claude knows what to search for.');
      return;
    }
    setModalExtracting(true);
    setModalExtractError(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL;
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${apiUrl}/vendors/ai-extract`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          vendorName: form.name,
          vendorWebsite: form.website || undefined,
          text: [
            form.vendor_phone ? `Phone: ${form.vendor_phone}` : null,
            form.vendor_email ? `Email: ${form.vendor_email}` : null,
          ].filter(Boolean).join('\n') || undefined,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        setModalExtractError(data.error || 'Extraction failed. Try again.');
        return;
      }
      const d = data.data as {
        name?: string | null;
        phone?: string | null;
        email?: string | null;
        website?: string | null;
        address?: string | null;
        hours_of_operation?: string | null;
        logo_url?: string | null;
        notes?: string | null;
        category?: string | null;
      };
      setForm((f) => ({
        ...f,
        name: f.name.trim() ? f.name : (d.name ?? f.name),
        vendor_phone: f.vendor_phone.trim() ? f.vendor_phone : (d.phone ?? f.vendor_phone),
        vendor_email: f.vendor_email.trim() ? f.vendor_email : (d.email ?? f.vendor_email),
        website: f.website.trim() ? f.website : (d.website ?? f.website),
        hours_of_operation: f.hours_of_operation.trim() ? f.hours_of_operation : (d.hours_of_operation ?? f.hours_of_operation),
        notes: f.notes.trim()
          ? (d.notes ? f.notes + '\n\n' + d.notes : f.notes)
          : (d.notes ?? f.notes),
        logo_url: f.logo_url ? f.logo_url : (d.logo_url ?? f.logo_url),
        service_categories: f.service_categories.length > 0
          ? f.service_categories
          : (d.category && catMap.has(d.category)
              ? [d.category]
              : f.service_categories),
      }));
    } catch (e) {
      console.error('extractForModal failed', e);
      setModalExtractError('Something went wrong. Please try again.');
    } finally {
      setModalExtracting(false);
    }
  }

  async function handleClearContract() {
    if (!editingVendor) return;
    setForm((f) => ({ ...f, contract_start_date: '', contract_expiry_date: '', contract_notes: '' }));
    try {
      await api.put(`/vendors/${editingVendor.id}`, {
        ...formToPayload(form),
        contract_start_date: null,
        contract_expiry_date: null,
        contract_notes: null,
      });
      qc.invalidateQueries({ queryKey: ['vendors'] });
      qc.invalidateQueries({ queryKey: ['vendors-expiring'] });
    } catch { /* silent */ }
  }

  async function handleSendEmail() {
    const emailVal = emailInputRef.current?.value || '';
    if (!emailVendor || !emailVal || !emailForm.subject || !emailForm.message) return;
    try {
      await api.post(`/vendors/${emailVendor.id}/email`, { to: emailVal, subject: emailForm.subject, message: emailForm.message });
      // Save to autocomplete history (max 20, deduped, most-recent-first)
      const stored = JSON.parse(localStorage.getItem('proqure_vendor_emails') || '[]') as string[];
      const updated = [emailVal, ...stored.filter((e) => e !== emailVal)].slice(0, 20);
      localStorage.setItem('proqure_vendor_emails', JSON.stringify(updated));
      setEmailVendor(null);
      if (emailInputRef.current) emailInputRef.current.value = '';
      setEmailForm({ to: '', subject: '', message: '' });
      setShowEmailSuggestions(false);
      showEmailToast('Vendor details shared.');
    } catch {
      showEmailToast('Failed to send vendor details', true);
    }
  }

  async function handleAiCompare() {
    setAiLoading(true);
    setAiResult(null);
    setAiComparisonId(null);
    setAiModalTab('new');
    setShowAiModal(true);
    try {
      const token = localStorage.getItem('accessToken');
      const facilityId = localStorage.getItem('selectedFacilityId') || '';
      const response = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/vendors/ai-compare`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-facility-id': facilityId,
        },
        body: JSON.stringify({}),
      });
      const json = await response.json();
      if (json.success && json.data) {
        setAiResult({
          rankings: json.data.rankings ?? [],
          summary: json.data.summary ?? '',
          vendorCount: json.data.vendorCount,
          batchCount: json.data.batchCount,
          comparisonId: json.data.comparisonId,
        });
        setAiComparisonId(json.data.comparisonId ?? null);
        qc.invalidateQueries({ queryKey: ['vendor-ai-comparisons'] });
      } else {
        setAiResult(null);
      }
    } catch {
      setAiResult(null);
    } finally {
      setAiLoading(false);
    }
  }

  async function handleViewComparison(compId: number) {
    setAiModalTab('new');
    setAiLoading(true);
    setAiResult(null);
    setAiComparisonId(null);
    setShowAiModal(true);
    try {
      const res = await api.get<{ id: number; rankings: AiRanking[]; summary: string | null; vendor_count: number; batch_count: number }>(`/vendors/ai-comparisons/${compId}`);
      const comp = res.data as any;
      if (comp) {
        setAiResult({
          rankings: comp.rankings ?? [],
          summary: comp.summary ?? '',
          vendorCount: comp.vendor_count,
          batchCount: comp.batch_count,
          comparisonId: comp.id,
        });
        setAiComparisonId(comp.id);
      }
    } catch {
      setAiResult(null);
    } finally {
      setAiLoading(false);
    }
  }

  async function handleDeleteComparison(compId: number) {
    try {
      await api.delete(`/vendors/ai-comparisons/${compId}`);
      qc.invalidateQueries({ queryKey: ['vendor-ai-comparisons'] });
    } catch { /* silent */ }
  }

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showModal]);

  // Keep slide panel in sync when vendors list refreshes
  useEffect(() => {
    if (slideVendor) {
      const updated = vendors.find((v) => v.id === slideVendor.id);
      if (updated) setSlideVendor(updated);
    }
  }, [vendors]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h2 className="page-title">Vendors</h2>
          <p className="page-subtitle">
            {isLoading ? 'Loading…' : `${vendors.length} vendor${vendors.length !== 1 ? 's' : ''} in directory`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            className="btn combo4-ai"
            onClick={isEnterprise ? handleAiCompare : () => { setShowEnterpriseHint(true); setTimeout(() => setShowEnterpriseHint(false), 5000); }}
            onMouseLeave={spinIconOut}
            style={{ opacity: isEnterprise ? 1 : 0.6 }}
          >
            {isEnterprise ? <Sparkles size={15} className="btn-icon" /> : <Lock size={15} className="btn-icon" />}
            AI Compare
            {!isEnterprise && (
              <span style={{ fontSize: 9, background: 'rgba(168,85,247,0.2)', color: '#d8b4fe', border: '1px solid rgba(168,85,247,0.3)', borderRadius: 99, padding: '1px 5px', marginLeft: 3 }}>
                Enterprise
              </span>
            )}
          </button>
          {['admin', 'org_admin', 'manager'].includes(user?.role ?? '') && (
            <button className="btn btn-ghost combo1" onClick={() => setShowManageServices(true)} onMouseLeave={spinIconOut}>
              <Settings2 size={15} className="btn-icon" /> Manage Services
            </button>
          )}
          <button className="btn combo5-hero" onClick={openAdd} onMouseLeave={spinIconOut}>
            <PlusSquare size={16} className="btn-icon" /> Add Vendor
          </button>
        </div>
      </div>

      {/* ─── Enterprise Upgrade Hint ─── */}
      {showEnterpriseHint && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '12px 16px', borderRadius: 'var(--radius-md)',
          background: 'rgba(168,85,247,0.12)', border: '1px solid rgba(168,85,247,0.35)',
          color: '#d8b4fe', marginBottom: 12, fontSize: '0.875rem',
        }}>
          <Lock size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>AI Vendor Compare is an Enterprise feature. Upgrade to Enterprise to analyze and rank all your vendors automatically.</span>
        </div>
      )}

      {/* ─── Email toast ─── */}
      <Toast toast={emailToast} />

      {/* ─── Pending Shared Vendors Banner (Pro/Enterprise) ─── */}
      {Array.isArray(pendingShared) && pendingShared.length > 0 && (
        <div style={{
          borderRadius: 'var(--radius-md)',
          background: 'rgba(245,158,11,0.12)', border: '1px solid #f59e0b',
          marginBottom: 16, fontSize: '0.875rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', color: '#f59e0b', fontWeight: 600 }}>
            <AlertTriangle size={15} style={{ flexShrink: 0 }} />
            ⚠ {pendingShared.length} vendor{pendingShared.length !== 1 ? 's' : ''} shared to your facility pending your approval
          </div>
          <div style={{ borderTop: '1px solid rgba(245,158,11,0.2)', padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pendingShared.map((v) => (
              <div key={v.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontWeight: 500, color: 'var(--text-primary)', fontSize: '0.875rem' }}>{v.name}</span>
                  {v.service_categories && v.service_categories.length > 0 && (
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{v.service_categories.join(', ')}</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ color: '#10b981', borderColor: 'rgba(16,185,129,0.25)' }}
                    disabled={approveMutation.isPending}
                    onClick={() => approveMutation.mutate(v.id)}
                  >
                    Approve
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ color: '#ef4444', borderColor: 'rgba(239,68,68,0.25)' }}
                    disabled={denyMutation.isPending}
                    onClick={() => setConfirmDenyShared(v)}
                  >
                    Deny
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Expiring Contracts Banner (Pro/Enterprise) ─── */}
      {isPro && expiringVendors.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 16px', borderRadius: 'var(--radius-md)',
          background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.35)',
          color: '#f59e0b', marginBottom: 20, fontSize: '0.875rem', fontWeight: 500,
        }}>
          <AlertTriangle size={16} style={{ flexShrink: 0 }} />
          <span>
            ⚠ {expiringVendors.length} vendor contract{expiringVendors.length !== 1 ? 's' : ''} expiring within 30 days:{' '}
            <strong>{expiringVendors.map((v) => v.name).join(', ')}</strong>
          </span>
        </div>
      )}

      {/* KPI Row */}
      <style>{`
        .kpi-card {
          background: rgba(255,255,255,0.045);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border-left: 1px solid rgba(255,255,255,0.08);
          border-right: 1px solid rgba(255,255,255,0.08);
          border-bottom: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          padding: 20px;
          position: relative;
          transition: all 0.2s ease;
        }
        .kpi-card:hover { transform: translateY(-2px); border-color: rgba(255,255,255,0.2); }
        .kpi-card:hover .kpi-icon { animation: jelloBounce 0.5s ease; }
        .kpi-icon-inner { display: inline-flex; transition: transform 0.4s ease; }
        .kpi-card:hover .kpi-icon-inner { animation: iconWiggle 0.45s ease; }
        @keyframes jelloBounce {
          0%, 100% { transform: scale(1,1); }
          30% { transform: scale(1.15, 0.85); }
          50% { transform: scale(0.85, 1.15); }
          70% { transform: scale(1.08, 0.92); }
          85% { transform: scale(0.97, 1.03); }
        }

        html[data-theme="light"] .kpi-card {
          background: #ffffff;
          border-left: 1px solid rgba(0,0,0,0.10);
          border-right: 1px solid rgba(0,0,0,0.10);
          border-bottom: 1px solid rgba(0,0,0,0.10);
        }
      `}</style>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(5, 1fr)', gap: isMobile ? '10px' : '16px', marginBottom: '24px' }}>
        {/* Total Vendors */}
        <div className="kpi-card" onMouseLeave={spinIconOut} style={{ borderTop: '3px solid #3b82f6' }}>
          <div className="kpi-icon" style={{ position: 'absolute', top: 16, right: 16 }}><span className="kpi-icon-inner btn-icon"><Users size={22} color="#3b82f6" /></span></div>
          <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 8 }}>Total Vendors</div>
          <div style={{ fontSize: 28, fontWeight: 600, color: '#3b82f6' }}><CountUp value={vendors.length} /></div>
        </div>
        {/* Main Vendors */}
        <div className="kpi-card" onMouseLeave={spinIconOut} style={{ borderTop: '3px solid #f59e0b' }}>
          <div className="kpi-icon" style={{ position: 'absolute', top: 16, right: 16 }}><span className="kpi-icon-inner btn-icon"><Building2 size={22} color="#f59e0b" /></span></div>
          <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 8 }}>Main Vendors</div>
          <div style={{ fontSize: 28, fontWeight: 600, color: '#f59e0b' }}><CountUp value={mainVendorCount} /></div>
        </div>
        {/* Service Categories */}
        <div className="kpi-card" onMouseLeave={spinIconOut} style={{ borderTop: '3px solid #8b5cf6' }}>
          <div className="kpi-icon" style={{ position: 'absolute', top: 16, right: 16 }}><span className="kpi-icon-inner btn-icon"><Settings2 size={22} color="#8b5cf6" /></span></div>
          <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 8 }}>Categories</div>
          <div style={{ fontSize: 28, fontWeight: 600, color: '#8b5cf6' }}><CountUp value={activeCategoryCount} /></div>
        </div>
        {/* 24/7 Available */}
        <div className="kpi-card" onMouseLeave={spinIconOut} style={{ borderTop: '3px solid #10b981' }}>
          <div className="kpi-icon" style={{ position: 'absolute', top: 16, right: 16 }}><span className="kpi-icon-inner btn-icon"><Clock size={22} color="#10b981" /></span></div>
          <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 8 }}>24/7 Available</div>
          <div style={{ fontSize: 28, fontWeight: 600, color: '#10b981' }}><CountUp value={available247Count} /></div>
        </div>
        {/* Preferred (Pro) */}
        {isPro ? (
          <div className="kpi-card" onMouseLeave={spinIconOut} style={{ borderTop: '3px solid #f59e0b', gridColumn: isMobile ? '1 / -1' : 'auto' }}>
            <div className="kpi-icon" style={{ position: 'absolute', top: 16, right: 16 }}><span className="kpi-icon-inner btn-icon"><Star size={22} color="#f59e0b" fill="#f59e0b" /></span></div>
            <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 8 }}>Preferred</div>
            <div style={{ fontSize: 28, fontWeight: 600, color: '#f59e0b' }}><CountUp value={vendors.filter((v) => v.is_preferred).length} /></div>
          </div>
        ) : (
          <div className="kpi-card" onMouseLeave={spinIconOut} style={{ borderTop: '3px solid rgba(59,130,246,0.4)', opacity: 0.7, cursor: 'default', gridColumn: isMobile ? '1 / -1' : 'auto' }}>
            <div className="kpi-icon" style={{ position: 'absolute', top: 16, right: 16 }}><span className="kpi-icon-inner btn-icon"><Lock size={18} color="rgba(59,130,246,0.4)" /></span></div>
            <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 8 }}>Preferred Vendors</div>
            <div style={{ fontSize: 28, fontWeight: 600 }}><Lock size={20} color="rgba(255,255,255,0.2)" /></div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.4 }}>Pro feature — upgrade to mark and sort preferred vendors</div>
          </div>
        )}
      </div>

      {/* Search + Category Tabs */}
      <div style={{ marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="search-bar search-input-wrapper" style={{ maxWidth: 340, flex: '1 1 220px' }}>
            <Search size={15} color="var(--text-muted)" className="btn-icon" />
            <input
              type="text"
              placeholder="Search vendors or contacts…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}
                onClick={() => setSearch('')}
              >
                <X size={14} />
              </button>
            )}
          </div>
          {isPro && (
            <div style={{ flexShrink: 0 }}>
              <DarkSelect
                value={vendorSort}
                onChange={(v) => setVendorSort(v as typeof vendorSort)}
                options={[
                  { value: 'name', label: 'Sort: Name' },
                  { value: 'highest_rated', label: 'Sort: Highest Rated' },
                  { value: 'lowest_rated', label: 'Sort: Lowest Rated' },
                  { value: 'most_reviewed', label: 'Sort: Most Reviewed' },
                ]}
                placeholder=""
              />
            </div>
          )}
        </div>

        {/* Category Filter — collapsible multi-select */}
        <div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* All button */}
            <button
              className={activeCategories.length === 0 ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
              onClick={() => setActiveCategories([])}
            >
              All
            </button>

            {/* Visible tags (first 8 collapsed, all when expanded) */}
            {(filterExpanded ? categoryTabs : categoryTabs.slice(0, 8)).map((cat) => {
              const sc = catMap.get(cat);
              const isActive = activeCategories.includes(cat);
              return (
                <button
                  key={cat}
                  className={`category-pill${isActive ? ' active' : ''}`}
                  onClick={() => toggleCategory(cat)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '4px 10px', borderRadius: 999,
                    fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer',
                    border: isActive
                      ? `2px solid ${sc?.color ?? 'var(--accent-primary)'}`
                      : '1px solid var(--border-subtle)',
                    background: isActive
                      ? `${sc?.color ?? 'var(--accent-primary)'}20`
                      : 'var(--bg-elevated)',
                    color: isActive ? (sc?.color ?? 'var(--accent-primary)') : 'var(--text-secondary)',
                    transition: 'all 150ms ease',
                  }}
                >
                  {sc && (
                    <span style={{
                      display: 'inline-block', width: 7, height: 7,
                      borderRadius: '50%', background: sc.color, flexShrink: 0,
                    }} />
                  )}
                  {cat}
                </button>
              );
            })}

            {/* Show more / Show less */}
            {categoryTabs.length > 8 && (
              <button
                className={`btn btn-ghost btn-sm show-all-btn${filterExpanded ? ' expanded' : ''}`}
                onClick={() => setFilterExpanded((f) => !f)}
                style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}
              >
                <span className="show-all-chevron">▼</span> {filterExpanded ? 'Show less' : `Show all (${categoryTabs.length})`}
              </button>
            )}
          </div>

          {/* Active-filter summary row */}
          {activeCategories.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {activeCategories.length} filter{activeCategories.length > 1 ? 's' : ''} active
                — {displayVendors.length} vendor{displayVendors.length !== 1 ? 's' : ''}
              </span>
              <button
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--accent-primary)', fontSize: '0.75rem', padding: 0,
                }}
                onClick={() => setActiveCategories([])}
              >
                Clear
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Vendor Grid */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
          <div className="loading-spinner" />
        </div>
      ) : displayVendors.length === 0 ? (
        <div className="empty-state">
          <Users size={48} />
          <h3>No vendors found</h3>
          <p>
            {search || activeCategories.length > 0
              ? 'Try adjusting your search or filter.'
              : 'Add vendors to track contacts, services, and schedules.'}
          </p>
          {!search && activeCategories.length === 0 && (
            <button className="btn btn-primary" onClick={openAdd}>
              <Plus size={16} /> Add Vendor
            </button>
          )}
        </div>
      ) : (
        <div className="vendor-grid">
          <style>{`
            @keyframes cardIn {
              from { opacity: 0; transform: translateY(12px); }
              to { opacity: 1; transform: translateY(0); }
            }
            @keyframes vRipple { to { transform: scale(2.5); opacity: 0; } }
            .vendor-card { transition: all 0.2s ease; animation: cardIn 0.35s ease backwards; }
            .vendor-card:hover {
              transform: translateY(-3px);
              border-color: rgba(59,130,246,0.4);
              box-shadow: 0 8px 24px rgba(0,0,0,0.3);
            }
            .vendor-card-footer { border-top: 1px solid rgba(255,255,255,0.06); }

            .vendor-action-edit:hover { background: rgba(255,255,255,0.08); }
            .vendor-action-sms:hover { background: rgba(16,185,129,0.15); }
            .vendor-action-share:hover { background: rgba(59,130,246,0.15); }
            .vendor-action-mail:hover { background: rgba(139,92,246,0.15); }

            html[data-theme="light"] .vendor-card {
              border: 1px solid rgba(0,0,0,0.12);
              background: #ffffff;
            }
            html[data-theme="light"] .vendor-card:hover {
              border-color: rgba(59,130,246,0.5);
              box-shadow: 0 8px 24px rgba(0,0,0,0.12);
            }
            html[data-theme="light"] .vendor-card-footer { border-top-color: rgba(0,0,0,0.08); }
            html[data-theme="light"] .vendor-action-edit {
              border-color: rgba(0,0,0,0.15);
              color: #6b7280;
            }
          `}</style>
          {displayVendors.map((vendor, index) => (
            <VendorCard
              key={vendor.id}
              vendor={vendor}
              catMap={catMap}
              isPro={isPro}
              isEnterprise={isEnterprise}
              highlighted={highlightVendorId === vendor.id}
              hasExpiringDocs={expiringDocVendorIds.has(vendor.id)}
              animationDelay={Math.min(index * 40, 400)}
              onOpen={() => setSlideVendor(vendor)}
              onEdit={() => openEdit(vendor)}
              onDelete={() => setConfirmDelete(vendor)}
              onTogglePreferred={() => preferredMutation.mutate(vendor.id)}
              onShare={() => { setShareVendor(vendor); setShareTargets([]); }}
              onEmail={() => { setEmailVendor(vendor); setEmailForm({ to: '', subject: `${vendor.name} — Contact Details`, message: `Vendor: ${vendor.name}\nCategory: ${(vendor.service_categories ?? []).join(', ') || 'N/A'}\nPhone: ${vendor.vendor_phone ?? vendor.contact_phone ?? 'N/A'}\nEmail: ${vendor.vendor_email ?? vendor.contact_email ?? 'N/A'}\nWebsite: ${vendor.website ?? 'N/A'}\nRep/Contact: ${vendor.contact_name ?? 'N/A'}\nNotes: ${vendor.notes ?? ''}` }); }}
            />
          ))}
        </div>
      )}

      {/* ─── Add / Edit Modal ──────────────────────────────────────────────── */}
      {showModal && ReactDOM.createPortal(
        <div
          className="modal-overlay"
          onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
          onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) closeModal(); mouseDownTarget.current = null; }}
        >
          <div className="modal" style={{ maxWidth: 600, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: 0 }}>
            {/* Fixed Header */}
            <div style={{ flexShrink: 0, padding: '24px 28px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="modal-header" style={{ marginBottom: 0 }}>
                <h3 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Building2 size={18} className="modal-icon-intro" style={{ color: '#3b82f6' }} />
                  {editingVendor ? 'Edit Vendor' : 'Add Vendor'}
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {(isPro || isEnterprise) && (
                    <button
                      type="button"
                      onClick={extractForModal}
                      disabled={modalExtracting}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        fontSize: '0.8rem', padding: '4px 10px', borderRadius: 7,
                        border: '1px solid rgba(139,92,246,0.35)',
                        background: modalExtracting ? 'rgba(139,92,246,0.05)' : 'rgba(139,92,246,0.12)',
                        color: '#a78bfa', cursor: modalExtracting ? 'not-allowed' : 'pointer',
                        opacity: modalExtracting ? 0.7 : 1, transition: 'all 0.15s',
                      }}
                    >
                      <Sparkles size={12} />
                      {modalExtracting ? 'Searching…' : 'AI Extract'}
                    </button>
                  )}
                  <button className="modal-close" onClick={closeModal}>
                    <X size={18} className="btn-icon" />
                  </button>
                </div>
              </div>
            </div>

            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
              <div style={{ overflowY: 'auto', overflowX: 'hidden', flex: 1, padding: '20px 28px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

                {/* ── Vendor Name + Flags ── */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'end' }}>
                  <div className="form-group">
                    <label className="form-label">Vendor Name *</label>
                    <input
                      className="form-input"
                      required
                      value={form.name}
                      onChange={(e) => {
                        setField('name', e.target.value);
                        if (modalExtractError) setModalExtractError(null);
                      }}
                      placeholder="e.g. ABC Plumbing & HVAC"
                    />
                  </div>
                  {modalExtractError && (
                    <div style={{
                      fontSize: '0.8rem', color: '#f87171', padding: '6px 10px',
                      background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
                      borderRadius: 7, marginTop: -8,
                    }}>
                      {modalExtractError}
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: 2 }}>
                    <CheckboxField
                      label="Main Vendor"
                      checked={form.is_main_vendor}
                      onChange={(v) => setField('is_main_vendor', v)}
                    />
                    <CheckboxField
                      label="24/7 Service"
                      checked={form.is_24_hour_service}
                      onChange={(v) => setField('is_24_hour_service', v)}
                    />
                  </div>
                </div>

                {/* ── Current Logo (Edit only) ── */}
                {editingVendor && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
                    {form.logo_url ? (
                      <>
                        <div
                          className="vendor-logo-wrapper"
                          style={{
                            background: 'rgba(128,128,128,0.08)',
                            borderRadius: 8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <img
                            src={getImageSrc(form.logo_url) ?? undefined}
                            alt="Vendor logo"
                            style={{
                              width: 64, height: 64, objectFit: 'contain', borderRadius: 8,
                              border: '1px solid var(--border-subtle)',
                            }}
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        </div>
                      </>
                    ) : (
                      <div style={{
                        width: 64, height: 64, borderRadius: 8,
                        background: getVendorColor(editingVendor?.id ?? 0) + '22',
                        border: `1px solid ${getVendorColor(editingVendor?.id ?? 0)}44`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '1.5rem', fontWeight: 700,
                        color: getVendorColor(editingVendor?.id ?? 0),
                      }}>
                        {form.name ? form.name.charAt(0).toUpperCase() : '?'}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Service Categories (searchable multi-select) ── */}
                <div className="form-group">
                  <label className="form-label">Service Categories</label>
                  <ServiceTagSelector
                    categories={serviceCategories}
                    selected={form.service_categories}
                    onChange={(cats) => setField('service_categories', cats)}
                  />
                </div>

                {/* ── Contacts ── */}
                <hr className="divider" style={{ margin: '0' }} />
                {editingVendor
                  ? <VendorContactsSection vendorId={editingVendor.id} isPro={isPro} vendor={editingVendor} />
                  : <LocalContactsSection isPro={isPro} contacts={newVendorContacts} onChange={setNewVendorContacts} />
                }

                <hr className="divider" style={{ margin: '0' }} />

                {/* ── Vendor Info ── */}
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 10 }}>
                    Vendor Info
                  </div>
                  <div className="vendor-form-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div className="form-group">
                      <label className="form-label">Vendor Phone</label>
                      <input
                        className="form-input"
                        value={form.vendor_phone}
                        onChange={(e) => setField('vendor_phone', e.target.value)}
                        placeholder="Main line"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Vendor Email</label>
                      <input
                        className="form-input"
                        type="email"
                        value={form.vendor_email}
                        onChange={(e) => setField('vendor_email', e.target.value)}
                        placeholder="info@vendor.com"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Account Number</label>
                      <input
                        className="form-input"
                        value={form.account_number}
                        onChange={(e) => setField('account_number', e.target.value)}
                        placeholder="Account #"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Hours of Operation</label>
                      <input
                        className="form-input"
                        value={form.hours_of_operation}
                        onChange={(e) => setField('hours_of_operation', e.target.value)}
                        placeholder="Mon–Fri 8am–5pm"
                        disabled={form.is_24_hour_service}
                      />
                    </div>
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label className="form-label">Website</label>
                      <input
                        className="form-input"
                        value={form.website}
                        onChange={(e) => setField('website', e.target.value)}
                        placeholder="https://vendor.com"
                      />
                    </div>
                  </div>
                </div>

                {/* ── Notes ── */}
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-textarea"
                    value={form.notes}
                    onChange={(e) => setField('notes', e.target.value)}
                    placeholder="Any additional notes…"
                    rows={3}
                  />
                </div>

                {/* ── Contract Info (Pro/Enterprise only) ── */}
                {isPro && (
                  <>
                    <hr className="divider" style={{ margin: '0' }} />
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <span style={CONTRACT_PILL}>Contract Info</span>
                        {editingVendor && (form.contract_start_date || form.contract_expiry_date || form.contract_notes) && (
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={handleClearContract}
                            style={{ fontSize: '0.75rem', color: 'var(--accent-danger)', borderColor: 'rgba(239,68,68,0.2)' }}
                          >
                            Clear Contract
                          </button>
                        )}
                      </div>
                      <div className="vendor-form-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div className="form-group">
                          <label className="form-label">Contract Start</label>
                          <DatePicker
                            value={form.contract_start_date}
                            onChange={(v) => setField('contract_start_date', v)}
                            placeholder="Select a date"
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Contract Expiry</label>
                          <DatePicker
                            value={form.contract_expiry_date}
                            onChange={(v) => setField('contract_expiry_date', v)}
                            placeholder="Select a date"
                          />
                        </div>
                        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                          <label className="form-label">Contract Notes</label>
                          <textarea
                            className="form-textarea"
                            value={form.contract_notes}
                            onChange={(e) => setField('contract_notes', e.target.value)}
                            placeholder="Contract terms, renewal notes…"
                            rows={2}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* ── Contract Files (Pro/Enterprise, Edit only) ── */}
                {isPro && editingVendor && (
                  <>
                    <hr className="divider" style={{ margin: '0' }} />
                    <VendorContractsSection
                      vendorId={editingVendor.id}
                      isPro={isPro}
                      isEnterprise={isEnterprise}
                    />
                  </>
                )}

              </div>
              </div>
              {/* Fixed Footer */}
              <div style={{ flexShrink: 0, padding: '16px 28px 20px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-ghost combo-cancel-jello" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary combo2" disabled={saving}>
                  {saving ? 'Saving…' : editingVendor ? 'Save Changes' : 'Add Vendor'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* ─── Delete Confirmation Modal ────────────────────────────────────── */}
      {confirmDelete && ReactDOM.createPortal(
        <div
          className="modal-overlay"
          onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
          onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) setConfirmDelete(null); mouseDownTarget.current = null; }}
        >
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Vendor</h3>
              <button className="modal-close" onClick={() => setConfirmDelete(null)}>
                <X size={18} className="btn-icon" />
              </button>
            </div>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
              Are you sure you want to delete <strong style={{ color: 'var(--text-primary)' }}>{confirmDelete.name}</strong>?
              This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost combo-cancel-jello" onClick={() => setConfirmDelete(null)}>
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={() => deleteMutation.mutate(confirmDelete.id)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete Vendor'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ─── Deny Shared Vendor Confirmation Modal ─────────────────────────── */}
      {confirmDenyShared && ReactDOM.createPortal(
        <div
          className="modal-overlay"
          onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
          onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) setConfirmDenyShared(null); mouseDownTarget.current = null; }}
        >
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h3 className="modal-title">Deny Shared Vendor</h3>
              <button className="modal-close" onClick={() => setConfirmDenyShared(null)}>
                <X size={18} className="btn-icon" />
              </button>
            </div>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
              Deny <strong style={{ color: 'var(--text-primary)' }}>{confirmDenyShared.name}</strong> from being added to your facility? This will remove the pending vendor record.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost combo-cancel-jello" onClick={() => setConfirmDenyShared(null)}>
                Cancel
              </button>
              <button
                className="btn btn-danger"
                disabled={denyMutation.isPending}
                onClick={() => {
                  denyMutation.mutate(confirmDenyShared.id);
                  setConfirmDenyShared(null);
                }}
              >
                {denyMutation.isPending ? 'Denying…' : 'Deny Vendor'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ─── Vendor Detail Slide-Out Panel ────────────────────────────────── */}
      <VendorDetailPanel
        vendor={slideVendor}
        catMap={catMap}
        isPro={isPro}
        isEnterprise={isEnterprise}
        isManagerOrAdmin={isManagerOrAdmin}
        onClose={() => setSlideVendor(null)}
        onEdit={() => slideVendor && openEdit(slideVendor)}
        onDelete={() => slideVendor && setConfirmDelete(slideVendor)}
        onLogoUploaded={(updated) => {
          setSlideVendor(updated);
          qc.invalidateQueries({ queryKey: ['vendors'] });
        }}
      />

      {/* ─── Share to Facility Modal ──────────────────────────────────────── */}
      {shareVendor && ReactDOM.createPortal(
        <div className="modal-overlay"
          onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
          onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) setShareVendor(null); mouseDownTarget.current = null; }}
        >
          <div className="modal" style={{ maxWidth: 440, maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: 0 }}>
            {/* Fixed Header */}
            <div style={{ flexShrink: 0, padding: '20px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 className="modal-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Share2 size={16} className="modal-icon-intro" style={{ color: '#3b82f6' }} /> Share to Facility
              </h3>
              <button className="modal-close" onClick={() => setShareVendor(null)}><X size={18} className="btn-icon" /></button>
            </div>
            {/* Scrollable body */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '20px 24px' }}>
              <p style={{ color: 'var(--text-secondary)', marginBottom: 16, fontSize: '0.875rem' }}>
                Share <strong style={{ color: 'var(--text-primary)' }}>{shareVendor.name}</strong> and its contacts to other facilities in your organization.
              </p>
              {sisterFacilities.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>No other facilities in your organization.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {sisterFacilities.map((f) => (
                    <label key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '8px 12px', borderRadius: 'var(--radius-sm)', border: `1px solid ${shareTargets.includes(f.id) ? 'var(--accent-primary)' : 'var(--border-subtle)'}`, background: shareTargets.includes(f.id) ? 'rgba(59,130,246,0.08)' : 'var(--bg-elevated)' }}>
                      <input
                        type="checkbox"
                        checked={shareTargets.includes(f.id)}
                        onChange={(e) => setShareTargets(e.target.checked ? [...shareTargets, f.id] : shareTargets.filter((id) => id !== f.id))}
                      />
                      <span style={{ fontSize: '0.875rem' }}>{f.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            {/* Fixed Footer */}
            <div style={{ flexShrink: 0, padding: '16px 24px 20px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost combo-cancel-jello" onClick={() => { setShareVendor(null); setShareTargets([]); }}>Cancel</button>
              <button
                className="btn btn-primary combo2"
                disabled={shareTargets.length === 0 || shareMutation.isPending}
                onClick={() => shareMutation.mutate({ id: shareVendor.id, targetFacilityIds: shareTargets })}
              >
                {shareMutation.isPending ? 'Sharing…' : `Share to ${shareTargets.length} Facilit${shareTargets.length === 1 ? 'y' : 'ies'}`}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ─── Share Vendor Details Modal ─────────────────────────────────── */}
      {emailVendor && ReactDOM.createPortal(
        <div className="modal-overlay"
          onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
          onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) { setEmailVendor(null); setShowEmailSuggestions(false); } mouseDownTarget.current = null; }}
        >
          <div className="modal" style={{ maxWidth: 480, maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: 0 }}>
            {/* Fixed Header */}
            <div style={{ flexShrink: 0, padding: '20px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Mail size={18} className="modal-icon-intro" style={{ color: '#6366f1', flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>Share Vendor Details</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{emailVendor.name}</div>
                </div>
              </div>
              <button className="modal-close" onClick={() => { setEmailVendor(null); setShowEmailSuggestions(false); }}><X size={18} className="btn-icon" /></button>
            </div>
            {/* Scrollable body */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '20px 24px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-group" style={{ position: 'relative' }}>
                  <label className="form-label">Recipient Email</label>
                  <input
                    ref={emailInputRef}
                    className="form-input"
                    type="email"
                    defaultValue=""
                    autoFocus
                    placeholder="recipient@example.com"
                    onChange={() => {
                      clearTimeout(suggestionTimerRef.current);
                      suggestionTimerRef.current = setTimeout(() => {
                        const val = emailInputRef.current?.value || '';
                        if (val.length < 1) { setEmailSuggestions([]); setShowEmailSuggestions(false); return; }
                        const stored = JSON.parse(localStorage.getItem('proqure_vendor_emails') || '[]') as string[];
                        const matches = stored.filter((e: string) => e.toLowerCase().startsWith(val.toLowerCase())).slice(0, 5);
                        setEmailSuggestions(matches);
                        setShowEmailSuggestions(matches.length > 0);
                      }, 300);
                    }}
                    onFocus={() => {
                      const val = emailInputRef.current?.value || '';
                      const stored = JSON.parse(localStorage.getItem('proqure_vendor_emails') || '[]') as string[];
                      const filtered = val
                        ? stored.filter((em) => em.toLowerCase().startsWith(val.toLowerCase())).slice(0, 5)
                        : stored.slice(0, 5);
                      setEmailSuggestions(filtered);
                      if (filtered.length > 0) setShowEmailSuggestions(true);
                    }}
                    onBlur={() => setTimeout(() => setShowEmailSuggestions(false), 150)}
                  />
                  {showEmailSuggestions && emailSuggestions.length > 0 && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10001,
                      background: 'rgba(20,30,50,0.97)',
                      border: '1px solid rgba(255,255,255,0.13)',
                      borderRadius: 8, overflow: 'hidden', marginTop: 2,
                      boxShadow: 'var(--shadow-md)',
                    }}>
                      {emailSuggestions.map((email) => (
                        <div
                          key={email}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            if (emailInputRef.current) emailInputRef.current.value = email;
                            setShowEmailSuggestions(false);
                          }}
                          style={{ padding: '9px 12px', fontSize: '0.875rem', cursor: 'pointer', color: 'var(--text-secondary)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'rgba(255,255,255,0.08)'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                        >
                          {email}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Subject</label>
                  <input className="form-input" value={emailForm.subject} onChange={(e) => setEmailForm((f) => ({ ...f, subject: e.target.value }))} placeholder="Subject" />
                </div>
                <div className="form-group">
                  <label className="form-label">Message</label>
                  <textarea className="form-textarea" rows={8} value={emailForm.message} onChange={(e) => setEmailForm((f) => ({ ...f, message: e.target.value }))} />
                </div>
              </div>
            </div>
            {/* Fixed Footer */}
            <div style={{ flexShrink: 0, padding: '16px 24px 20px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost combo-cancel-jello" onClick={() => { setEmailVendor(null); setShowEmailSuggestions(false); }}>Cancel</button>
              <button className="btn btn-primary combo2" disabled={!emailForm.subject || !emailForm.message} onClick={handleSendEmail}>
                <Mail size={14} className="btn-icon" /> Send Email
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ─── AI Vendor Comparison Modal ───────────────────────────────────── */}
      {showAiModal && ReactDOM.createPortal(
        <div className="modal-overlay"
          onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
          onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) setShowAiModal(false); mouseDownTarget.current = null; }}
        >
          <div className="modal" style={{ maxWidth: 600, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: 0 }}>
            {/* Fixed Header */}
            <div style={{ flexShrink: 0, padding: '20px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 className="modal-title" style={{ margin: 0 }}>
                <Sparkles size={16} className="modal-icon-intro" style={{ verticalAlign: 'middle', marginRight: 6, color: '#a855f7' }} />
                AI Vendor Comparison
              </h3>
              <button className="modal-close" onClick={() => setShowAiModal(false)}><X size={18} className="btn-icon" /></button>
            </div>
            {/* Scrollable body */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '20px 24px' }}>
              {/* Pill tabs */}
              <div style={{ display: 'inline-flex', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 10, padding: 3, marginBottom: 16 }}>
                <button
                  onClick={() => setAiModalTab('new')}
                  style={{
                    fontSize: 12, fontWeight: 500, padding: '5px 12px', borderRadius: 7, border: 'none', cursor: 'pointer',
                    background: aiModalTab === 'new' ? 'rgba(255,255,255,0.15)' : 'transparent',
                    color: aiModalTab === 'new' ? 'var(--text-primary)' : 'var(--text-secondary)',
                  }}
                >
                  New Comparison
                </button>
                <button
                  onClick={() => setAiModalTab('history')}
                  style={{
                    fontSize: 12, fontWeight: 500, padding: '5px 12px', borderRadius: 7, border: 'none', cursor: 'pointer',
                    background: aiModalTab === 'history' ? 'rgba(255,255,255,0.15)' : 'transparent',
                    color: aiModalTab === 'history' ? 'var(--text-primary)' : 'var(--text-secondary)',
                  }}
                >
                  History
                </button>
              </div>

              {aiModalTab === 'new' ? (
                aiLoading ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '48px 0' }}>
                    <div className="loading-spinner" />
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Analyzing vendors in batches…</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>This may take 20–40 seconds</div>
                  </div>
                ) : !aiResult ? (
                  <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '24px 0' }}>AI comparison failed. Please try again.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {/* Meta info */}
                    {(aiResult.vendorCount != null || aiResult.batchCount != null) && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', gap: 16 }}>
                        {aiResult.vendorCount != null && <span>{aiResult.vendorCount} vendor{aiResult.vendorCount !== 1 ? 's' : ''} analyzed</span>}
                        {aiResult.batchCount != null && <span>{aiResult.batchCount} batch{aiResult.batchCount !== 1 ? 'es' : ''}</span>}
                        {aiResult.comparisonId != null && <span style={{ color: 'rgba(168,85,247,0.7)' }}>Saved #{aiResult.comparisonId}</span>}
                      </div>
                    )}
                    {/* Summary */}
                    {aiResult.summary && (
                      <div style={{ padding: '12px 16px', borderRadius: 'var(--radius-sm)', background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.2)', fontSize: '0.875rem', lineHeight: 1.6, color: 'var(--text-secondary)' }}>
                        {aiResult.summary}
                      </div>
                    )}
                    {/* Rankings */}
                    {aiResult.rankings.map((r) => (
                      <div key={r.vendor_name} style={{ padding: '14px 16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                          <span style={{ fontWeight: 700, fontSize: '0.9375rem', flex: 1 }}>{r.vendor_name}</span>
                          <span style={{ display: 'flex', gap: 2 }}>
                            {[1, 2, 3, 4, 5].map((i) => (
                              <Star key={i} size={14} color="#f59e0b" fill={i <= Math.round(r.rating) ? '#f59e0b' : 'none'} />
                            ))}
                          </span>
                        </div>
                        {r.strengths?.length > 0 && (
                          <div style={{ marginBottom: 6 }}>
                            <div style={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--accent-success)', marginBottom: 3 }}>Strengths</div>
                            {r.strengths.map((s, i) => <div key={i} style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>• {s}</div>)}
                          </div>
                        )}
                        {r.concerns?.length > 0 && (
                          <div style={{ marginBottom: 6 }}>
                            <div style={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--accent-warning)', marginBottom: 3 }}>Concerns</div>
                            {r.concerns.map((c, i) => <div key={i} style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>• {c}</div>)}
                          </div>
                        )}
                        {r.recommendation && (
                          <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: 4 }}>{r.recommendation}</div>
                        )}
                      </div>
                    ))}
                    <div style={{ textAlign: 'center', fontSize: '0.6875rem', color: 'var(--text-muted)', paddingBottom: 4 }}>Powered by Claude</div>
                  </div>
                )
              ) : (
                savedComparisons.length === 0 ? (
                  <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '32px 0', fontSize: '0.875rem' }}>No saved comparisons yet.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {savedComparisons.map((c) => (
                      <div key={c.id} style={{ padding: '12px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: 2 }}>{c.title}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', gap: 12 }}>
                            <span>{c.vendor_count} vendor{c.vendor_count !== 1 ? 's' : ''}</span>
                            <span>{new Date(c.created_at).toLocaleDateString()}</span>
                          </div>
                          {c.summary && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any }}>
                              {c.summary}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ color: '#a855f7', borderColor: 'rgba(168,85,247,0.2)' }}
                            onClick={() => handleViewComparison(c.id)}
                          >
                            <Eye size={13} /> View
                          </button>
                          {['admin', 'org_admin', 'manager'].includes(user?.role ?? '') && (
                            <button
                              className="btn btn-ghost btn-sm"
                              style={{ color: '#ef4444', borderColor: 'rgba(239,68,68,0.2)' }}
                              onClick={() => handleDeleteComparison(c.id)}
                            >
                              <Trash2 size={13} /> Delete
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
            {/* Fixed Footer */}
            {aiModalTab === 'new' && !aiLoading && aiResult && (
              <div style={{ flexShrink: 0, padding: '14px 24px 18px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => window.print()}
                  title="Download / Print as PDF"
                >
                  <FileText size={14} /> Download PDF
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ color: '#a855f7', borderColor: 'rgba(168,85,247,0.2)' }}
                  onClick={handleAiCompare}
                >
                  <Sparkles size={14} /> Re-analyze
                </button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* ─── Manage Services Modal ────────────────────────────────────────── */}
      {showManageServices && (
        <ManageServicesModal
          onClose={() => setShowManageServices(false)}
          onChanged={() => qc.invalidateQueries({ queryKey: ['service-categories'] })}
        />
      )}
    </div>
  );
}

// ─── Vendor Card ──────────────────────────────────────────────────────────────

const VENDOR_COLORS = [
  '#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6',
  '#ec4899','#06b6d4','#84cc16','#f97316','#14b8a6',
  '#6366f1','#a855f7','#e11d48','#0ea5e9','#22c55e',
  '#eab308','#f43f5e','#7c3aed','#0891b2','#65a30d',
];
const getVendorColor = (id: number) => VENDOR_COLORS[id % VENDOR_COLORS.length];
function getVendorInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return words.slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '?';
}

const addRipple = (e: ReactMouseEvent<HTMLButtonElement>) => {
  const btn = e.currentTarget;
  const circle = document.createElement('span');
  const d = Math.max(btn.clientWidth, btn.clientHeight);
  const rect = btn.getBoundingClientRect();
  circle.style.cssText = `position:absolute;border-radius:50%;background:rgba(96,165,250,0.35);width:${d}px;height:${d}px;left:${e.clientX - rect.left - d / 2}px;top:${e.clientY - rect.top - d / 2}px;transform:scale(0);animation:vRipple 0.5s ease-out forwards;pointer-events:none;`;
  btn.appendChild(circle);
  setTimeout(() => circle.remove(), 500);
};

interface VendorCardProps {
  vendor: Vendor;
  catMap: Map<string, ServiceCategory>;
  isPro: boolean;
  isEnterprise: boolean;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onTogglePreferred: () => void;
  onShare: () => void;
  onEmail: () => void;
  highlighted?: boolean;
  hasExpiringDocs?: boolean;
  animationDelay?: number;
}

function VendorCard({ vendor, catMap, isPro, isEnterprise, onOpen, onEdit, onDelete, onTogglePreferred, onShare, onEmail, highlighted, hasExpiringDocs, animationDelay }: VendorCardProps) {
  const phone = vendor.vendor_phone;
  const email = vendor.vendor_email;
  const [expanded, setExpanded] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsPhone, setSmsPhone] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsMsg, setSmsMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [showUpgradeHint, setShowUpgradeHint] = useState<string | null>(null);
  const upgradeHintTimerRef = useRef<ReturnType<typeof setTimeout>>();

  async function handleSendSms() {
    if (!smsPhone.trim()) return;
    setSmsSending(true);
    setSmsMsg(null);
    try {
      await api.post(`/vendors/${vendor.id}/share-sms`, { phone: smsPhone.trim() });
      setSmsMsg({ text: 'SMS sent!', ok: true });
      setTimeout(() => { setSmsOpen(false); setSmsPhone(''); setSmsMsg(null); }, 1500);
    } catch (err: any) {
      const msg = err?.response?.data?.error ?? 'Failed to send SMS. Check Twilio config.';
      setSmsMsg({ text: msg, ok: false });
    } finally {
      setSmsSending(false);
    }
  }

  const tagsToShow = expanded ? vendor.service_categories : vendor.service_categories.slice(0, 4);
  const overflow = vendor.service_categories.length - 4;

  const vendorColor = getVendorColor(vendor.id);
  const vendorInitials = getVendorInitials(vendor.name);
  const missingFieldCount = [
    !vendor.hours_of_operation && !vendor.is_24_hour_service,
    !email,
    !vendor.website,
    !vendor.notes,
  ].filter(Boolean).length;

  return (
    <div
      id={`vendor-card-${vendor.id}`}
      className={`card vendor-card${highlighted ? ' vendor-highlight-glow' : ''}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        cursor: 'pointer',
        position: 'relative',
        animationDelay: `${animationDelay ?? 0}ms`,
      }}
      onClick={onOpen}
    >
      {/* ── Header row ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {/* Vendor avatar — logo or initials */}
        <div style={{ width: 56, height: 56, borderRadius: 12, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {vendor.logo_url ? (
            <img
              src={getImageSrc(vendor.logo_url) ?? undefined}
              alt={`${vendor.name} logo`}
              style={{
                width: 56, height: 56, objectFit: 'contain',
                borderRadius: 12, border: '1px solid rgba(255,255,255,0.10)',
              }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          ) : (
            <div style={{
              width: 56, height: 56, borderRadius: 12,
              background: `${vendorColor}22`, border: `1px solid ${vendorColor}44`,
              color: vendorColor, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, fontWeight: 700,
            }}>
              {vendorInitials}
            </div>
          )}
        </div>

        {/* Name + contact (flex 1) */}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Building2 size={14} color="var(--text-muted)" style={{ flexShrink: 0 }} />
            <h4 className="vendor-name" style={{ margin: 0, fontSize: '0.9375rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {vendor.name}
            </h4>
          </div>
          {(vendor.primary_contact_name ?? vendor.contact_name) ? (
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 3 }}>
              {vendor.primary_contact_name ?? vendor.contact_name}
            </div>
          ) : (
            <div style={{ fontSize: '0.8rem', color: '#4b5563', fontStyle: 'italic', marginTop: 3 }}>
              No contact on file
            </div>
          )}
        </div>

        {/* Status badges + preferred star — stacked, never overlapping */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end', flexShrink: 0 }}>
          {isPro && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!vendor.is_preferred) {
                  const icon = e.currentTarget.querySelector('.star-icon') as HTMLElement | null;
                  if (icon) {
                    icon.classList.add('favoriting');
                    setTimeout(() => icon.classList.remove('favoriting'), 400);
                  }
                }
                onTogglePreferred();
              }}
              title={vendor.is_preferred ? 'Remove preferred' : 'Mark as preferred'}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                display: 'flex', alignItems: 'center',
              }}
            >
              <Star
                className="star-icon"
                size={16}
                color="#f59e0b"
                fill={vendor.is_preferred ? '#f59e0b' : 'none'}
              />
            </button>
          )}
          {vendor.is_24_hour_service && <span className="badge badge-green">24/7</span>}
          {vendor.is_main_vendor && <span className="badge badge-amber">Main</span>}
        </div>
      </div>

      {/* ── Service Category Tags ── */}
      {vendor.service_categories.length > 0 && (
        <div
          style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}
          onClick={(e) => e.stopPropagation()}
        >
          {tagsToShow.map((cat) => (
            <ServicePill key={cat} name={cat} category={catMap.get(cat)} />
          ))}
          {!expanded && overflow > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
              style={{
                padding: '2px 8px',
                borderRadius: 999,
                fontSize: '0.6875rem',
                fontWeight: 500,
                cursor: 'pointer',
                border: '1px solid var(--border-subtle)',
                background: 'var(--bg-elevated)',
                color: 'var(--text-muted)',
              }}
            >
              +{overflow} more
            </button>
          )}
          {expanded && overflow > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
              style={{
                padding: '2px 8px',
                borderRadius: 999,
                fontSize: '0.6875rem',
                fontWeight: 500,
                cursor: 'pointer',
                border: '1px solid var(--border-subtle)',
                background: 'var(--bg-elevated)',
                color: 'var(--text-muted)',
              }}
            >
              show less
            </button>
          )}
        </div>
      )}

      {/* ── Star Rating (shown when rated) ── */}
      {(() => {
        const vwr = vendor as VendorWithRating;
        const avgRating = parseFloat(vwr.avg_rating ?? '') || 0;
        const ratingCount = vwr.rating_count ?? 0;
        if (avgRating === 0 || ratingCount === 0) return null;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ display: 'flex', gap: 1 }}>
              {[1, 2, 3, 4, 5].map((i) => {
                const full = avgRating >= i - 0.25;
                const half = !full && avgRating >= i - 0.75;
                if (full) return <span key={i} style={{ fontSize: 13, color: '#f59e0b', lineHeight: 1 }}>★</span>;
                if (half) return (
                  <span key={i} style={{ position: 'relative', display: 'inline-block', fontSize: 13, color: 'rgba(255,255,255,0.2)', lineHeight: 1 }}>
                    ★<span style={{ position: 'absolute', left: 0, top: 0, width: '50%', overflow: 'hidden', color: '#f59e0b' }}>★</span>
                  </span>
                );
                return <span key={i} style={{ fontSize: 13, color: 'rgba(255,255,255,0.2)', lineHeight: 1 }}>★</span>;
              })}
            </div>
            <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
              {avgRating.toFixed(1)} ({ratingCount})
            </span>
          </div>
        );
      })()}

      {/* ── Contract Expiry Badge ── */}
      {isPro && (() => {
        const status = contractExpiryStatus(vendor.contract_expiry_date);
        if (!status) return null;
        const cfg = {
          ok: { bg: 'rgba(16,185,129,0.12)', color: '#10b981', text: 'Contract ✓' },
          soon: { bg: 'rgba(245,158,11,0.12)', color: '#f59e0b', text: 'Expires soon' },
          expired: { bg: 'rgba(239,68,68,0.12)', color: '#ef4444', text: 'Expired' },
        }[status];
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <span style={{
              display: 'inline-block', padding: '2px 8px', borderRadius: 999,
              fontSize: '0.625rem', fontWeight: 700,
              background: cfg.bg, color: cfg.color,
            }}>{cfg.text}</span>
          </div>
        );
      })()}

      {/* ── Contact Details ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
        {phone && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <Phone size={13} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
            <a href={`tel:${phone}`} onClick={(e) => e.stopPropagation()} style={{ color: 'inherit' }}>{phone}</a>
          </div>
        )}
        {email && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <Mail size={13} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
            <a href={`mailto:${email}`} onClick={(e) => e.stopPropagation()} style={{ color: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {email}
            </a>
          </div>
        )}
        {vendor.website && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <Globe size={13} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
            <a
              href={safeWebsiteUrl(vendor.website) ?? '#'}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--accent-primary)' }}
            >
              {vendor.website.replace(/^https?:\/\/(www\.)?/, '')}
            </a>
          </div>
        )}
        {vendor.hours_of_operation && !vendor.is_24_hour_service && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <Clock size={13} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
            {vendor.hours_of_operation}
          </div>
        )}
        {vendor.account_number && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <Hash size={13} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
            <span style={{ color: 'var(--text-muted)' }}>Acct:</span>&nbsp;{vendor.account_number}
          </div>
        )}
        {missingFieldCount >= 2 && (
          <div
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            style={{ fontSize: 12, color: '#4b5563', fontStyle: 'italic', cursor: 'pointer' }}
          >
            + Add hours, email, website...
          </div>
        )}
      </div>

      {/* ── Notes ── */}
      {vendor.notes && (
        <div
          style={{
            fontSize: '0.75rem',
            color: 'var(--text-muted)',
            background: 'var(--bg-base)',
            borderRadius: 'var(--radius-sm)',
            padding: '6px 10px',
            borderLeft: '2px solid var(--border-strong)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={vendor.notes}
        >
          {vendor.notes}
        </div>
      )}

      {/* ── Risk Flags (Enterprise) ── */}
      {isEnterprise && vendor.risk_flags && vendor.risk_flags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }} onClick={(e) => e.stopPropagation()}>
          {vendor.risk_flags.map((flag) => (
            <span key={flag} style={{
              background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.4)',
              color: '#fcd34d', fontSize: 11, padding: '2px 7px', borderRadius: 99,
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
              <AlertTriangle size={10} /> {flag}
            </span>
          ))}
        </div>
      )}

      {/* ── Expiring Document Badge (Pro+) ── */}
      {isPro && hasExpiringDocs && (
        <div onClick={(e) => e.stopPropagation()}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 8px', borderRadius: 999, fontSize: '0.625rem', fontWeight: 700,
            background: 'rgba(245,158,11,0.12)', color: '#f59e0b',
          }}>
            <FileText size={9} /> Doc expiring
          </span>
        </div>
      )}

      {/* ── Footer: action icons + view details ── */}
      <div
        className="vendor-card-footer"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: 'auto', paddingTop: 12,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              className="combo-jello-spin vendor-action-edit"
              data-tint="neutral"
              onClick={(e) => { addRipple(e); onEdit(); }}
              onMouseLeave={spinIconOut}
              title="Edit"
              style={{
                width: 30, height: 30, borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
                border: '1px solid rgba(107,114,128,0.4)', color: '#6b7280',
              }}
            >
              <Pencil size={14} className="btn-icon" />
            </button>
            <button
              className="combo-jello-spin vendor-action-sms"
              data-tint="green"
              onClick={(e) => {
                addRipple(e);
                if (isPro) { setSmsPhone(''); setSmsMsg(null); setSmsOpen(true); }
                else {
                  clearTimeout(upgradeHintTimerRef.current);
                  setShowUpgradeHint('SMS');
                  upgradeHintTimerRef.current = setTimeout(() => setShowUpgradeHint(null), 3000);
                }
              }}
              onMouseLeave={spinIconOut}
              title="Send SMS"
              style={{
                width: 30, height: 30, borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: isPro ? 1 : 0.5, cursor: isPro ? 'pointer' : 'not-allowed',
                border: '1px solid rgba(16,185,129,0.4)', color: '#34d399',
              }}
            >
              <MessageSquare size={14} className="btn-icon" />
            </button>
            <button
              className="combo-jello-spin vendor-action-share"
              data-tint="blue"
              onClick={(e) => {
                addRipple(e);
                if (isPro) onShare();
                else {
                  clearTimeout(upgradeHintTimerRef.current);
                  setShowUpgradeHint('Share');
                  upgradeHintTimerRef.current = setTimeout(() => setShowUpgradeHint(null), 3000);
                }
              }}
              onMouseLeave={spinIconOut}
              title="Share"
              style={{
                width: 30, height: 30, borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: isPro ? 1 : 0.5, cursor: isPro ? 'pointer' : 'not-allowed',
                border: '1px solid rgba(59,130,246,0.4)', color: '#60a5fa',
              }}
            >
              <Share2 size={14} className="btn-icon" />
            </button>
            <button
              className="combo-jello-spin vendor-action-mail"
              data-tint="purple"
              onClick={(e) => {
                addRipple(e);
                if (isPro) onEmail();
                else {
                  clearTimeout(upgradeHintTimerRef.current);
                  setShowUpgradeHint('Email');
                  upgradeHintTimerRef.current = setTimeout(() => setShowUpgradeHint(null), 3000);
                }
              }}
              onMouseLeave={spinIconOut}
              title="Email"
              style={{
                width: 30, height: 30, borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: isPro ? 1 : 0.5, cursor: isPro ? 'pointer' : 'not-allowed',
                border: '1px solid rgba(139,92,246,0.4)', color: '#a78bfa',
              }}
            >
              <Mail size={14} className="btn-icon" />
            </button>
          </div>
          {showUpgradeHint && (
            <div style={{ fontSize: '0.75rem', color: '#fbbf24' }}>
              Upgrade to Pro to unlock {showUpgradeHint}.
            </div>
          )}
        </div>
        <button
          className="view-details-link"
          onClick={onOpen}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 11, color: '#60a5fa', padding: 0, flexShrink: 0,
          }}
        >
          View details <ArrowRight size={12} className="arrow-icon" />
        </button>
      </div>

      {/* ── SMS Modal ── */}
      {smsOpen && ReactDOM.createPortal(
        <div
          className="modal-overlay"
          onClick={() => { setSmsOpen(false); setSmsMsg(null); }}
        >
          <div
            className="modal"
            style={{ maxWidth: 420, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Fixed Header */}
            <div style={{ flexShrink: 0, padding: '20px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Smartphone size={18} className="modal-icon-intro" style={{ color: '#10b981', flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>Share via SMS</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{vendor.name}</div>
                </div>
              </div>
              <button className="modal-close" onClick={() => { setSmsOpen(false); setSmsMsg(null); }}>
                <X size={18} className="btn-icon" />
              </button>
            </div>
            {/* Body */}
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label className="form-label">Recipient phone number</label>
              <input
                className="form-input"
                autoFocus
                placeholder="+1 555 000 0000"
                value={smsPhone}
                onChange={(e) => setSmsPhone(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSendSms(); }}
              />
              <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                Include country code for international numbers (e.g. +44 for UK). US numbers work without it.
              </p>
              {smsMsg && (
                <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: smsMsg.ok ? '#10b981' : '#ef4444' }}>
                  {smsMsg.text}
                </div>
              )}
            </div>
            {/* Fixed Footer */}
            <div style={{ flexShrink: 0, padding: '12px 24px 20px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost combo-cancel-jello" onClick={() => { setSmsOpen(false); setSmsMsg(null); }}>Cancel</button>
              <button className="btn btn-primary combo2" onClick={handleSendSms} disabled={smsSending || !smsPhone.trim()}>
                {smsSending ? 'Sending…' : 'Send SMS'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Service Pill ─────────────────────────────────────────────────────────────

function ServicePill({ name, category }: { name: string; category?: ServiceCategory }) {
  const color = category?.color ?? '#6366f1';
  return (
    <span
      style={{
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: '0.6875rem',
        fontWeight: 500,
        border: `1px solid ${color}44`,
        background: `${color}22`,
        color: color,
        display: 'inline-block',
      }}
    >
      {name}
    </span>
  );
}

// ─── Local Contacts Section (Add-mode: buffers contacts before vendor exists) ──

function LocalContactsSection({ isPro, contacts, onChange }: {
  isPro: boolean;
  contacts: ContactForm[];
  onChange: (c: ContactForm[]) => void;
}) {
  const [addForm, setAddForm] = useState<ContactForm | null>(null);
  const [editIndex, setEditIndex] = useState<number | null>(null);

  const contactLimitReached = !isPro && contacts.length >= 1;

  const typeColors: Record<string, string> = {
    primary: '#3b82f6', secondary: '#10b981', emergency: '#ef4444', after_hours: '#f59e0b',
  };
  const typeLabels: Record<string, string> = {
    primary: 'Primary', secondary: 'Secondary', emergency: 'Emergency', after_hours: 'After Hours',
  };

  function handleSaveLocal() {
    if (!addForm || !addForm.name.trim()) return;
    if (editIndex !== null) {
      const updated = [...contacts];
      updated[editIndex] = addForm;
      onChange(updated);
    } else {
      onChange([...contacts, addForm]);
    }
    setAddForm(null);
    setEditIndex(null);
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div className="panel-section-label" style={{ marginBottom: 0 }}><span style={CONTACT_PILL}>Contacts</span></div>
        {addForm === null && !contactLimitReached && (
          <button
            type="button"
            className="btn btn-ghost btn-sm combo1"
            onClick={() => { setAddForm(EMPTY_CONTACT); setEditIndex(null); }}
            style={{ fontSize: '0.75rem' }}
          >
            <Plus size={12} className="btn-icon" /> Add Contact
          </button>
        )}
      </div>

      {contacts.length === 0 && !addForm && (
        <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', margin: 0 }}>No contacts added yet.</p>
      )}

      {contacts.map((c, idx) => {
        const color = typeColors[c.contact_type] ?? '#6366f1';
        return (
          <div key={idx} style={{
            padding: '10px 12px', borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)',
            marginBottom: 6, display: 'flex', flexDirection: 'column', gap: 4,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 600, fontSize: '0.875rem', flex: 1 }}>{c.name}</span>
              <span style={{
                padding: '1px 7px', borderRadius: 999, fontSize: '0.6875rem', fontWeight: 700,
                background: `${color}22`, color, border: `1px solid ${color}44`,
              }}>{typeLabels[c.contact_type] ?? c.contact_type}</span>
              <button
                type="button"
                className="btn btn-ghost btn-sm combo1"
                onClick={() => { setEditIndex(idx); setAddForm({ ...c }); }}
                style={{ padding: '2px 6px' }}
              ><Pencil size={12} className="btn-icon" /></button>
              <button
                type="button"
                className="btn btn-sm combo3-danger"
                onClick={() => onChange(contacts.filter((_, i) => i !== idx))}
                style={{ padding: '2px 6px' }}
              ><Trash2 size={12} className="btn-icon" /></button>
            </div>
            {c.title && <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{c.title}</div>}
            {c.phone && (
              <div style={{ fontSize: '0.8rem', display: 'flex', gap: 5, alignItems: 'center', color: 'var(--text-secondary)' }}>
                <Phone size={11} style={{ color: 'var(--text-muted)' }} />{c.phone}
              </div>
            )}
            {c.email && (
              <div style={{ fontSize: '0.8rem', display: 'flex', gap: 5, alignItems: 'center', color: 'var(--text-secondary)' }}>
                <Mail size={11} style={{ color: 'var(--text-muted)' }} />{c.email}
              </div>
            )}
          </div>
        );
      })}

      {contactLimitReached && !addForm && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: 4 }}>
          <Lock size={12} style={{ flexShrink: 0 }} />
          One contact per vendor — upgrade to Pro for multiple contacts.
        </div>
      )}

      {addForm !== null && (
        <div style={{ padding: '12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-default)', background: 'var(--bg-base)', display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Name *</label>
              <input className="form-input" style={{ fontSize: '0.8125rem' }} value={addForm.name} onChange={(e) => setAddForm((f) => f && ({ ...f, name: e.target.value }))} placeholder="Full name" autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Title</label>
              <input className="form-input" style={{ fontSize: '0.8125rem' }} value={addForm.title} onChange={(e) => setAddForm((f) => f && ({ ...f, title: e.target.value }))} placeholder="Role or title" />
            </div>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Type</label>
              <select className="form-input" style={{ fontSize: '0.8125rem' }} value={addForm.contact_type} onChange={(e) => setAddForm((f) => f && ({ ...f, contact_type: e.target.value as ContactForm['contact_type'] }))}>
                <option value="primary">Primary</option>
                <option value="secondary">Secondary</option>
                <option value="emergency">Emergency</option>
                <option value="after_hours">After Hours</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Phone</label>
              <input className="form-input" style={{ fontSize: '0.8125rem' }} value={addForm.phone} onChange={(e) => setAddForm((f) => f && ({ ...f, phone: e.target.value }))} placeholder="(555) 000-0000" />
            </div>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Email</label>
              <input className="form-input" type="email" style={{ fontSize: '0.8125rem' }} value={addForm.email} onChange={(e) => setAddForm((f) => f && ({ ...f, email: e.target.value }))} placeholder="email@vendor.com" />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Notes</label>
              <textarea className="form-textarea" style={{ fontSize: '0.8125rem' }} rows={2} value={addForm.notes} onChange={(e) => setAddForm((f) => f && ({ ...f, notes: e.target.value }))} placeholder="Optional notes…" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setAddForm(null); setEditIndex(null); }}>Cancel</button>
            <button type="button" className="btn btn-primary btn-sm" disabled={!addForm.name.trim()} onClick={handleSaveLocal}>
              {editIndex !== null ? 'Save Changes' : 'Add Contact'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Vendor Contracts Section (Edit modal, Pro/Enterprise only) ───────────────

type VendorContract = {
  id: number;
  vendor_id: number;
  file_url: string;
  file_name: string | null;
  version: number;
  uploaded_at: string;
  ai_analysis: any | null;
  ai_analyzed_at: string | null;
  notes: string | null;
  service_label: string | null;
  sort_order: number | null;
};

type ContractComparison = {
  summary: string;
  price_changes: string;
  scope_changes: string;
  new_clauses: string[];
  removed_clauses: string[];
  changed_terms: { field: string; old_value: string; new_value: string }[];
  overall_assessment: string;
  recommendation: 'renew' | 'negotiate' | 'reject';
};

// Hoisted out of VendorContractsSection (was a local const there) so SortableContractRow below --
// a separate top-level component, required since useSortable is a hook and can't be called inside
// a .map() callback -- can use it too. Pure function, no closures, safe to hoist as-is.
function riskColor(risk: string) {
  if (risk === 'high') return '#ef4444';
  if (risk === 'medium') return '#f59e0b';
  return '#10b981';
}

// One row within a service-type group's card, drag-to-reorder via @dnd-kit (CLAUDE_RULES.md:
// "Drag to reorder — use @dnd-kit only"). Extracted out of VendorContractsSection's own .map()
// specifically so useSortable (a hook) has a real component to run in -- hooks can't be called
// inside a loop/callback. attributes/listeners are spread ONLY on the small grip handle, not the
// whole row (same split Tasks.tsx's SortableTaskRow uses), so the row's own buttons/PDF link stay
// independently clickable instead of fighting the drag gesture for the same pointer events.
function SortableContractRow({
  contract, expandedAnalysis, setExpandedAnalysis, analyzingId, isEnterprise, handleAnalyze, setDeleteContractId,
}: {
  contract: VendorContract;
  expandedAnalysis: number | null;
  setExpandedAnalysis: (id: number | null) => void;
  analyzingId: number | null;
  isEnterprise: boolean;
  handleAnalyze: (contract: VendorContract, force?: boolean) => void;
  setDeleteContractId: (id: number | null) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: contract.id });
  const wrapperStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition ?? undefined,
    opacity: isDragging ? 0.5 : 1,
    position: isDragging ? 'relative' : undefined,
    zIndex: isDragging ? 999 : undefined,
  };
  return (
    <div ref={setNodeRef} style={wrapperStyle}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        background: 'var(--bg-elevated)', borderRadius: 8,
        padding: '8px 12px', border: '1px solid var(--border)',
      }}>
        <div
          {...attributes} {...listeners}
          style={{ cursor: 'grab', touchAction: 'none', display: 'flex', alignItems: 'center', color: 'var(--text-muted)', flexShrink: 0 }}
          title="Drag to reorder"
        >
          <GripVertical size={14} />
        </div>
        <FileText size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <PdfViewerButton
            pdfUrl={getVendorProxyUrl(contract.file_url, contract.file_name || 'contract.pdf')}
            label={contract.file_name ?? `Contract v${contract.version}`}
          />
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {new Date(contract.uploaded_at).toLocaleDateString()}
          </div>
        </div>
        <span style={{
          fontSize: '0.7rem', fontWeight: 700, padding: '2px 6px',
          background: 'rgba(59,130,246,0.15)', color: '#3b82f6',
          borderRadius: 4, flexShrink: 0,
        }}>
          v{contract.version}
        </span>
        {contract.ai_analysis && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setExpandedAnalysis(expandedAnalysis === contract.id ? null : contract.id)}
            style={{ flexShrink: 0, padding: '2px 8px', fontSize: '0.75rem' }}
          >
            {expandedAnalysis === contract.id ? 'Hide' : 'Analysis'}
          </button>
        )}
        {isEnterprise ? (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => handleAnalyze(contract, !!contract.ai_analysis)}
            disabled={analyzingId === contract.id}
            style={{ flexShrink: 0, padding: '2px 8px', fontSize: '0.75rem', color: '#a855f7', borderColor: 'rgba(168,85,247,0.3)' }}
          >
            {analyzingId === contract.id
              ? 'Analyzing…'
              : <><Sparkles size={11} /> {contract.ai_analysis ? 'Re-analyze' : 'Analyze'}</>}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            title="Enterprise feature"
            style={{ flexShrink: 0, padding: '2px 8px', fontSize: '0.75rem', color: 'var(--text-muted)' }}
          >
            <Lock size={11} /> Analyze
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            const filename = contract.file_name
              ? (contract.file_name.toLowerCase().endsWith('.pdf')
                  ? contract.file_name
                  : contract.file_name + '.pdf')
              : `contract-v${contract.version}.pdf`;
            openVendorFile(contract.file_url, filename);
          }}
          style={{ background: 'none', border: 'none', outline: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '3px 6px', display: 'inline-flex', alignItems: 'center' }}
          title="View contract"
        >
          <Eye size={14} />
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => setDeleteContractId(contract.id)}
          style={{ flexShrink: 0, padding: '2px 8px', color: 'var(--accent-danger)' }}
          title="Delete contract"
        >
          <Trash2 size={13} />
        </button>
      </div>

      {/* AI Analysis Panel */}
      {expandedAnalysis === contract.id && contract.ai_analysis && (
        <div style={{
          margin: '6px 0 0', padding: 14,
          background: 'rgba(168,85,247,0.06)', border: '1px solid rgba(168,85,247,0.2)',
          borderRadius: 8, fontSize: '0.82rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <Sparkles size={14} style={{ color: '#a855f7' }} />
            <span style={{ fontWeight: 600, color: '#a855f7' }}>AI Contract Analysis</span>
            {(contract.ai_analysis.risk_level || contract.ai_analysis.overall_risk) && (
              <span style={{
                marginLeft: 'auto', fontSize: '0.72rem', fontWeight: 700,
                padding: '2px 8px', borderRadius: 4,
                background: `${riskColor((contract.ai_analysis.risk_level || contract.ai_analysis.overall_risk).toLowerCase())}22`,
                color: riskColor((contract.ai_analysis.risk_level || contract.ai_analysis.overall_risk).toLowerCase()),
              }}>
                {(contract.ai_analysis.risk_level || contract.ai_analysis.overall_risk).toUpperCase()} RISK
              </span>
            )}
          </div>
          {contract.ai_analysis.summary && (
            <p style={{ margin: '0 0 10px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{contract.ai_analysis.summary}</p>
          )}
          {contract.ai_analysis.risk_explanation && (
            <p style={{ margin: '0 0 10px', color: 'var(--text-muted)', fontSize: '0.8rem', lineHeight: 1.5, fontStyle: 'italic' }}>{contract.ai_analysis.risk_explanation}</p>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 14px', marginBottom: 10 }}>
            {[
              ['Effective Date', contract.ai_analysis.effective_date],
              ['Expiry Date', contract.ai_analysis.expiry_date],
              ['Payment Terms', contract.ai_analysis.payment_terms],
              ['Renewal Terms', contract.ai_analysis.renewal_terms],
              ['Termination', contract.ai_analysis.termination || contract.ai_analysis.termination_clause],
              ['Liability Cap', contract.ai_analysis.liability_cap],
              ['Price Escalation', contract.ai_analysis.price_escalation],
            ].filter(([, v]) => v).map(([label, value]) => (
              <div key={label}>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: 2 }}>{label}</div>
                <div style={{ color: 'var(--text-primary)' }}>{value}</div>
              </div>
            ))}
          </div>
          {(contract.ai_analysis.obligations || contract.ai_analysis.key_obligations)?.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: 4, fontWeight: 600 }}>KEY OBLIGATIONS</div>
              {(contract.ai_analysis.obligations || contract.ai_analysis.key_obligations).map((o: string, i: number) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 3 }}>
                  <span style={{ color: '#10b981', flexShrink: 0 }}>✓</span>
                  <span style={{ color: 'var(--text-secondary)' }}>{o}</span>
                </div>
              ))}
            </div>
          )}
          {contract.ai_analysis.red_flags?.length > 0 && (
            <div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: 4, fontWeight: 600 }}>RED FLAGS</div>
              {contract.ai_analysis.red_flags.map((f: string, i: number) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 3 }}>
                  <span style={{ color: '#ef4444', flexShrink: 0 }}>⚠</span>
                  <span style={{ color: 'var(--text-secondary)' }}>{f}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Hardens `result.error || fallback` against more than plain undefined/null/empty-string --
// specifically a whitespace-only string (truthy in JS, so `||` alone would let it through and
// render as a visibly empty toast bubble) and a non-string `error` value (an unexpected shape
// from some path this file doesn't control, e.g. a framework-level error handler responding
// instead of this route's own try/catch). Every known server error path already sends a real,
// non-empty string, so this is defense in depth for whatever caused the empty-toast report, not a
// fix for a reproduced cause -- traced every current error path (analyze, compare's two AI calls,
// upload) and all of them return a real message; this just makes "toast text can never be blank"
// true unconditionally instead of true "as far as I can trace."
function errText(result: unknown, fallback: string): string {
  const err = (result as { error?: unknown } | null | undefined)?.error;
  return typeof err === 'string' && err.trim() ? err : fallback;
}

function VendorContractsSection({ vendorId, isPro, isEnterprise }: { vendorId: number; isPro: boolean; isEnterprise: boolean }) {
  const qc = useQueryClient();
  const { toast, showToast } = useToast();
  const [uploadingContract, setUploadingContract] = useState(false);
  const [analyzingId, setAnalyzingId] = useState<number | null>(null);
  const [expandedAnalysis, setExpandedAnalysis] = useState<number | null>(null);
  const [deleteContractId, setDeleteContractId] = useState<number | null>(null);
  const [compareModal, setCompareModal] = useState(false);
  const [compareId1, setCompareId1] = useState<string>('');
  const [compareId2, setCompareId2] = useState<string>('');
  const [comparing, setComparing] = useState(false);
  const [compareResult, setCompareResult] = useState<ContractComparison | null>(null);
  const [serviceLabel, setServiceLabel] = useState('');
  const [compareServiceLabel, setCompareServiceLabel] = useState('');
  const contractFileRef = useRef<HTMLInputElement>(null);
  const mouseDownTarget = useRef<EventTarget | null>(null);

  const { data: contractsRes, refetch: refetchContracts } = useQuery({
    queryKey: ['vendor-contracts', vendorId],
    queryFn: () => api.get<VendorContract[]>(`/vendors/${vendorId}/contracts`),
    enabled: isPro,
  });
  const contracts: VendorContract[] = (contractsRes?.data ?? []) as VendorContract[];

  // Local drag order, synced from the server list (already sort_order-ordered, v136) but able to
  // move optimistically on drag before the PATCH/refetch round-trip completes -- same pattern
  // Tasks.tsx's orderedTasks uses for its own drag-reorder. Keeps whatever a still-present
  // contract's local position is, drops removed ones, appends newly-arrived ones (a fresh upload)
  // at the end rather than dropping the user's in-progress local order on the next refetch.
  const [orderedContracts, setOrderedContracts] = useState<VendorContract[]>([]);
  useEffect(() => {
    setOrderedContracts(prev => {
      const currentIds = new Set(contracts.map(c => c.id));
      const prevKept = prev.filter(c => currentIds.has(c.id));
      const prevIds = new Set(prevKept.map(c => c.id));
      const updated = prevKept.map(c => contracts.find(f => f.id === c.id) ?? c);
      const newOnes = contracts.filter(c => !prevIds.has(c.id));
      return [...updated, ...newOnes];
    });
  }, [contractsRes]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: servicesRes } = useQuery({
    queryKey: ['service-categories'],
    queryFn: () => api.get<ServiceCategory[]>('/vendors/service-categories'),
  });
  const services: ServiceCategory[] = (servicesRes?.data ?? []) as ServiceCategory[];

  useEffect(() => {
    if (contracts.length > 0 && expandedAnalysis === null) {
      const first = contracts.find(c => c.ai_analysis);
      if (first) setExpandedAnalysis(first.id);
    }
  }, [contracts]);

  // Recently-used service types FOR THIS VENDOR (not a global most-used list) -- `contracts` is
  // already scoped to this one vendorId (the query key above), so this is just that vendor's own
  // upload history, most recent first, deduped, capped at 2. No new endpoint/query needed.
  const recentServiceLabels = [...contracts]
    .sort((a, b) => new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime())
    .map(c => c.service_label)
    .filter((label, i, arr): label is string => !!label && arr.indexOf(label) === i)
    .slice(0, 2);

  // One card per service type instead of one per upload -- built off orderedContracts (not raw
  // `contracts`) so each group's own item order reflects the persisted sort_order / any in-flight
  // local drag, not just upload recency. Building groups by first-appearance order puts whichever
  // service type's own top item sorts first as that group's own card position. Untitled/empty
  // service_label uploads share an "Uncategorized" group rather than each getting their own
  // single-item card.
  const groupedContracts = orderedContracts.reduce<{ label: string; items: VendorContract[] }[]>((groups, c) => {
    const label = c.service_label || 'Uncategorized';
    const existing = groups.find(g => g.label === label);
    if (existing) existing.items.push(c); else groups.push({ label, items: [c] });
    return groups;
  }, []);

  const reorderContractMut = useMutation({
    mutationFn: ({ contractId, sort_order }: { contractId: number; sort_order: number }) =>
      api.patch(`/vendors/${vendorId}/contracts/${contractId}`, { sort_order }),
  });

  // Scoped to one group (service type) at a time -- reorders only that group's own slice of
  // orderedContracts, leaving every other group's items at their existing array positions. Only
  // PATCHes the contracts whose position within the group actually changed, same
  // only-mutate-what-moved pattern as Tasks.tsx's own drag-reorder.
  function handleGroupDragEnd(groupLabel: string, event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setOrderedContracts(prev => {
      const groupIds = prev.filter(c => (c.service_label || 'Uncategorized') === groupLabel).map(c => c.id);
      const oldIndex = groupIds.indexOf(active.id as number);
      const newIndex = groupIds.indexOf(over.id as number);
      if (oldIndex === -1 || newIndex === -1) return prev;
      const reorderedGroupIds = arrayMove(groupIds, oldIndex, newIndex);
      let gi = 0;
      const next = prev.map(c => {
        if ((c.service_label || 'Uncategorized') !== groupLabel) return c;
        const id = reorderedGroupIds[gi++];
        return prev.find(p => p.id === id)!;
      });
      reorderedGroupIds.forEach((id, idx) => {
        const original = prev.find(p => p.id === id);
        if (original && original.sort_order !== idx) {
          reorderContractMut.mutate({ contractId: id, sort_order: idx });
        }
      });
      return next;
    });
  }

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      alert('Only PDF, DOC, and DOCX files are allowed');
      return;
    }
    setUploadingContract(true);
    try {
      const formData = new FormData();
      formData.append('contract', file);
      if (serviceLabel.trim()) formData.append('service_label', serviceLabel.trim());
      const baseUrl = import.meta.env.VITE_API_URL ?? '';
      const token = localStorage.getItem('accessToken');
      const resp = await fetch(`${baseUrl}/vendors/${vendorId}/contracts`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      const result = await resp.json();
      if (result.success) {
        await refetchContracts();
        qc.invalidateQueries({ queryKey: ['vendor-contracts', vendorId] });
        const v = result.data?.version ?? '?';
        // Was a `window.dispatchEvent(new CustomEvent('proqure:toast', ...))` -- there has never
        // been a listener for that event anywhere in the client (grepped the whole client/src),
        // so this "toast" has never actually shown to a single user. Real pattern (useToast/Toast,
        // already used elsewhere in this same file/component) below instead.
        showToast(`Contract uploaded (v${v})`);
      } else {
        showToast(errText(result, 'Contract upload failed'), true);
      }
    } catch (err) {
      console.error('[contracts/upload]', err);
      showToast('Contract upload failed', true);
    } finally {
      setUploadingContract(false);
      setServiceLabel('');
      if (contractFileRef.current) contractFileRef.current.value = '';
    }
  };

  const handleAnalyze = async (contract: VendorContract, force?: boolean) => {
    const id = contract.id;
    setAnalyzingId(id);
    try {
      const baseUrl = import.meta.env.VITE_API_URL ?? '';
      const token = localStorage.getItem('accessToken');
      const resp = await fetch(`${baseUrl}/vendors/${vendorId}/contracts/${id}/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(force ? { forceRegenerate: true } : {}),
      });
      const result = await resp.json();
      if (result.success) {
        setExpandedAnalysis(id);
        await refetchContracts();
        showToast(result.cached ? 'Loaded existing analysis' : 'Contract analyzed');
      } else {
        // THE actual root cause of the "click did nothing" bug: this branch never existed before
        // -- a non-2xx or {success:false} response (403 wrong plan tier, 404 contract not found,
        // 500 PDF-fetch failure, 500 Anthropic error/timeout -- server/src/routes/vendors.ts,
        // POST /:id/contracts/:contractId/analyze) fell all the way through with NO else, so the
        // button's "Analyzing…" state (which was already there, and did work) just cleared itself
        // a beat later via the finally below and nothing else ever happened -- indistinguishable
        // from the click not registering at all. Checked and ruled out both of the theories in the
        // request: contract identity here is `contract.id` end to end (button onClick, this
        // function's param, the query string, the server's WHERE clause) never a positional array
        // index, and there is no in-flight/dedupe lock anywhere in this file OR the server route --
        // analyzingId only ever disables the ONE button matching that contract's own id. The
        // Anthropic call (claude-opus-5 on a full PDF) is exactly the kind of request that
        // occasionally 429s/529s/times out, which combined with this missing branch reproduces
        // the reported pattern exactly: fails silently, an unrelated contract's click succeeds,
        // fails silently again, then a plain retry succeeds -- normal transient-API-error
        // behavior that nothing on screen ever surfaced. Now it does.
        showToast(errText(result, 'Contract analysis failed'), true);
      }
    } catch (err) {
      console.error('[contracts/analyze]', err);
      showToast('Contract analysis failed', true);
    } finally {
      setAnalyzingId(null);
    }
  };

  const handleDelete = async (contractId: number) => {
    try {
      await api.delete(`/vendors/${vendorId}/contracts/${contractId}`);
      await refetchContracts();
      setDeleteContractId(null);
    } catch (err) {
      console.error('[contracts/delete]', err);
    }
  };

  const handleCompare = async () => {
    if (!compareId1 || !compareId2 || compareId1 === compareId2) return;
    setComparing(true);
    setCompareResult(null);
    try {
      const baseUrl = import.meta.env.VITE_API_URL ?? '';
      const token = localStorage.getItem('accessToken');
      const resp = await fetch(`${baseUrl}/vendors/${vendorId}/contracts/compare`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ contractId1: Number(compareId1), contractId2: Number(compareId2) }),
      });
      const result = await resp.json();
      // Same missing-else-branch bug as handleAnalyze had (fixed last round) -- a 403/404/502
      // response fell through with zero user-facing effect, "Comparing..." just cleared itself a
      // beat later via finally below. Same fix pattern: a real else branch, using whatever message
      // the server sends (now a specific one from callClaudeForJson server-side, not a generic
      // "AI comparison failed" -- see vendors.ts).
      if (result.success) {
        setCompareResult(result.data);
        showToast('Contracts compared');
      } else {
        showToast(errText(result, 'Contract comparison failed'), true);
      }
    } catch (err) {
      console.error('[contracts/compare]', err);
      showToast('Contract comparison failed', true);
    } finally {
      setComparing(false);
    }
  };

  // riskColor hoisted to module scope above (SortableContractRow needs it too, as a separate
  // top-level component) -- was a local const here, now shared instead of duplicated.
  const recColor = (rec: string) => {
    if (rec === 'reject') return '#ef4444';
    if (rec === 'negotiate') return '#f59e0b';
    return '#10b981';
  };

  return (
    <div>
      <Toast toast={toast} />
      <div style={{ marginBottom: 12 }}>
        {/* Row 1: Badge */}
        <div className="panel-section-label" style={{ marginBottom: 8 }}><span style={CONTRACT_PILL}>Contract Files</span></div>
        {/* Quick-picks: the last couple of service types actually used FOR THIS VENDOR (recent
            uploads, not a global/app-wide most-used list) -- one click sets serviceLabel without
            opening the full select below. Hidden entirely once one's already picked/typed, or if
            this vendor has no upload history yet to draw quick-picks from. */}
        {recentServiceLabels.length > 0 && !serviceLabel && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Recent for this vendor:</span>
            {recentServiceLabels.map(label => (
              <button
                key={label}
                type="button"
                onClick={() => setServiceLabel(label)}
                // Same missing-outline-reset bug as ServiceTagSelector's chip remove button (this
                // round's item 3) -- proactively fixed here too since it's the identical unstyled-
                // button pattern I added last round, not because Chris reported this exact spot.
                style={{
                  fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 999,
                  background: 'rgba(59,130,246,0.12)', color: '#7dabf5',
                  border: '1px solid rgba(59,130,246,0.3)', outline: 'none', cursor: 'pointer',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        {/* Row 2: Service type + Upload button on same row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <select
            value={serviceLabel}
            onChange={e => setServiceLabel(e.target.value)}
            style={{
              flex: 1,
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-default)',
              borderRadius: 6,
              color: 'var(--text-primary)',
              fontSize: 13,
              padding: '7px 10px',
            }}
          >
            <option value="">— Service type (optional) —</option>
            {services.map(s => (
              <option key={s.id} value={s.name}>{s.name}</option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => contractFileRef.current?.click()}
            disabled={uploadingContract}
            style={{ flexShrink: 0 }}
          >
            {uploadingContract ? <span style={{ fontSize: '0.75rem' }}>Uploading…</span> : <><Upload size={12} /> Upload Contract</>}
          </button>
          <input
            ref={contractFileRef}
            type="file"
            accept=".pdf,.doc,.docx"
            style={{ display: 'none' }}
            onChange={handleUpload}
          />
        </div>
        {/* Row 3: Compare Versions button on its own row */}
        {isEnterprise && contracts.length >= 2 && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => { setCompareModal(true); setCompareResult(null); setCompareId1(''); setCompareId2(''); setCompareServiceLabel(''); }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', width: 'auto', marginBottom: 4 }}
          >
            <Sparkles size={12} style={{ color: '#a855f7' }} /> Compare Versions
          </button>
        )}
      </div>

      {contracts.length === 0 && (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', textAlign: 'center', padding: '12px 0' }}>
          No contracts uploaded yet.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {groupedContracts.map((group) => (
          <div key={group.label} className="card" style={{ padding: 10 }}>
            {/* Group header -- same green pill the old per-row service_label badge used, now
                shown once per service type instead of repeated on every one of its contracts. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{
                fontSize: '0.75rem', fontWeight: 600, padding: '3px 9px',
                background: 'rgba(16,185,129,0.12)', color: '#6ee7b7',
                borderRadius: 4, border: '1px solid rgba(16,185,129,0.25)',
              }}>
                {group.label}
              </span>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                {group.items.length} contract{group.items.length === 1 ? '' : 's'}
              </span>
            </div>
            {/* Drag-to-reorder scoped to THIS group only -- a separate DndContext per service
                type, not one shared across the whole list, so dnd-kit's own collision detection
                never considers a different group's rows as drop targets. Reordering one service
                type's contracts can't accidentally reshuffle a different one. */}
            <DndContext collisionDetection={closestCenter} onDragEnd={(e) => handleGroupDragEnd(group.label, e)}>
              <SortableContext items={group.items.map(c => c.id)} strategy={verticalListSortingStrategy}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {group.items.map((contract) => (
                    <SortableContractRow
                      key={contract.id}
                      contract={contract}
                      expandedAnalysis={expandedAnalysis}
                      setExpandedAnalysis={setExpandedAnalysis}
                      analyzingId={analyzingId}
                      isEnterprise={isEnterprise}
                      handleAnalyze={handleAnalyze}
                      setDeleteContractId={setDeleteContractId}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        ))}
      </div>

      {/* Delete Confirmation */}
      {deleteContractId !== null && (
        <div className="modal-overlay"
          onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
          onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) setDeleteContractId(null); mouseDownTarget.current = null; }}
        >
          <div className="modal" style={{ maxWidth: 380 }}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Contract</h3>
              <button className="modal-close" onClick={() => setDeleteContractId(null)}><X size={18} className="btn-icon" /></button>
            </div>
            <div style={{ padding: '16px 20px' }}>
              <p style={{ color: 'var(--text-secondary)', marginBottom: 20 }}>
                Are you sure you want to delete this contract? This cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-ghost combo-cancel-jello" onClick={() => setDeleteContractId(null)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={() => handleDelete(deleteContractId)}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compare Versions Modal */}
      {compareModal && (
        <div className="modal-overlay"
          onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
          onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) setCompareModal(false); mouseDownTarget.current = null; }}
        >
          <div className="modal" style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h3 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Sparkles size={16} className="modal-icon-intro" style={{ color: '#a855f7' }} /> Compare Contract Versions
              </h3>
              <button className="modal-close" onClick={() => setCompareModal(false)}><X size={18} className="btn-icon" /></button>
            </div>
            <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              {(() => {
                const serviceLabels = Array.from(new Set(contracts.map(c => c.service_label).filter(Boolean))) as string[];
                const filteredContracts = compareServiceLabel
                  ? contracts.filter(c => c.service_label === compareServiceLabel)
                  : contracts;
                return (
                  <>
                    {serviceLabels.length > 0 && (
                      <div className="form-group">
                        <label className="form-label">Service Type</label>
                        <select
                          className="form-input"
                          value={compareServiceLabel}
                          onChange={(e) => { setCompareServiceLabel(e.target.value); setCompareId1(''); setCompareId2(''); }}
                          style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
                        >
                          <option value="">All contracts</option>
                          {serviceLabels.map(l => (
                            <option key={l} value={l}>{l}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div className="form-group">
                        <label className="form-label">Version A</label>
                        <select
                          className="form-input"
                          value={compareId1}
                          onChange={(e) => setCompareId1(e.target.value)}
                          style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
                        >
                          <option value="">Select version…</option>
                          {filteredContracts.map((c) => (
                            <option key={c.id} value={String(c.id)}>v{c.version} — {c.file_name ?? `Contract v${c.version}`}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Version B</label>
                        <select
                          className="form-input"
                          value={compareId2}
                          onChange={(e) => setCompareId2(e.target.value)}
                          style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
                        >
                          <option value="">Select version…</option>
                          {filteredContracts.map((c) => (
                            <option key={c.id} value={String(c.id)}>v{c.version} — {c.file_name ?? `Contract v${c.version}`}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </>
                );
              })()}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!compareId1 || !compareId2 || compareId1 === compareId2 || comparing}
                  onClick={handleCompare}
                >
                  {comparing ? 'Comparing…' : <><Sparkles size={13} /> Compare</>}
                </button>
              </div>

              {compareResult && (
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Comparison Result</span>
                    <span style={{
                      marginLeft: 'auto', fontSize: '0.75rem', fontWeight: 700,
                      padding: '3px 10px', borderRadius: 4,
                      background: `${recColor(compareResult.recommendation)}22`,
                      color: recColor(compareResult.recommendation),
                      textTransform: 'uppercase',
                    }}>
                      {compareResult.recommendation}
                    </span>
                  </div>
                  {compareResult.summary && <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.85rem', lineHeight: 1.5 }}>{compareResult.summary}</p>}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {compareResult.price_changes && (
                      <div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: 3, fontWeight: 600 }}>PRICE CHANGES</div>
                        <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{compareResult.price_changes}</div>
                      </div>
                    )}
                    {compareResult.scope_changes && (
                      <div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: 3, fontWeight: 600 }}>SCOPE CHANGES</div>
                        <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{compareResult.scope_changes}</div>
                      </div>
                    )}
                  </div>
                  {compareResult.changed_terms?.length > 0 && (
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: 6, fontWeight: 600 }}>CHANGED TERMS</div>
                      {compareResult.changed_terms.map((t, i) => (
                        <div key={i} style={{ marginBottom: 6, fontSize: '0.82rem' }}>
                          <span style={{ fontWeight: 600 }}>{t.field}:</span>
                          <span style={{ color: '#ef4444', marginLeft: 6, textDecoration: 'line-through' }}>{t.old_value}</span>
                          <span style={{ color: '#10b981', marginLeft: 6 }}>→ {t.new_value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {compareResult.new_clauses?.length > 0 && (
                    <div>
                      <div style={{ fontSize: '0.7rem', color: '#10b981', marginBottom: 4, fontWeight: 600 }}>NEW CLAUSES</div>
                      {compareResult.new_clauses.map((c, i) => (
                        <div key={i} style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: 3 }}>+ {c}</div>
                      ))}
                    </div>
                  )}
                  {compareResult.removed_clauses?.length > 0 && (
                    <div>
                      <div style={{ fontSize: '0.7rem', color: '#ef4444', marginBottom: 4, fontWeight: 600 }}>REMOVED CLAUSES</div>
                      {compareResult.removed_clauses.map((c, i) => (
                        <div key={i} style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: 3 }}>- {c}</div>
                      ))}
                    </div>
                  )}
                  {compareResult.overall_assessment && (
                    <div style={{ background: 'var(--bg-elevated)', borderRadius: 8, padding: 12, fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                      {compareResult.overall_assessment}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Vendor Contacts Section (shared: Add/Edit modal + Detail Panel) ──────────

function VendorContactsSection({ vendorId, isPro, vendor }: { vendorId: number; isPro: boolean; vendor?: Vendor }) {
  const qc = useQueryClient();
  const [contactForm, setContactForm] = useState<ContactForm | null>(null);
  const [editingContact, setEditingContact] = useState<VendorContact | null>(null);
  const [deleteContactId, setDeleteContactId] = useState<number | null>(null);
  const [contactSaving, setContactSaving] = useState(false);
  const autoMigrated = useRef(false);

  const { data: contactsRes, isSuccess, refetch: refetchContacts } = useQuery({
    queryKey: ['vendor-contacts', vendorId],
    queryFn: () => api.get<VendorContact[]>(`/vendors/${vendorId}/contacts`),
    enabled: true,
  });
  const contacts: VendorContact[] = (contactsRes?.data ?? []) as VendorContact[];

  // Auto-migrate legacy contact fields → vendor_contacts on first open
  useEffect(() => {
    if (!isPro || !isSuccess || autoMigrated.current) return;
    if (contacts.length > 0) return;
    if (!vendor) return;
    const hasLegacy = vendor.contact_name || vendor.contact_phone || vendor.contact_email;
    if (!hasLegacy) return;
    autoMigrated.current = true;
    api.post(`/vendors/${vendorId}/contacts`, {
      name: vendor.contact_name || 'Primary Contact',
      phone: vendor.contact_phone || null,
      email: vendor.contact_email || null,
      contact_type: 'primary',
      title: '',
      notes: '',
    }).then(() => {
      refetchContacts();
      qc.invalidateQueries({ queryKey: ['vendor-contacts', vendorId] });
    }).catch(() => { autoMigrated.current = false; });
  }, [isPro, isSuccess, contacts.length, vendor, vendorId, refetchContacts, qc]);

  async function handleSaveContact() {
    if (!contactForm) return;
    if (!contactForm.name.trim()) return;
    setContactSaving(true);
    try {
      if (editingContact) {
        await api.put(`/vendors/${vendorId}/contacts/${editingContact.id}`, contactForm);
      } else {
        await api.post(`/vendors/${vendorId}/contacts`, contactForm);
      }
      await refetchContacts();
      qc.invalidateQueries({ queryKey: ['vendor-contacts', vendorId] });
      setContactForm(null);
      setEditingContact(null);
    } finally {
      setContactSaving(false);
    }
  }

  async function handleDeleteContact(contactId: number) {
    await api.delete(`/vendors/${vendorId}/contacts/${contactId}`);
    await refetchContacts();
    setDeleteContactId(null);
  }

  const contactLimitReached = !isPro && contacts.length >= 1;

  const typeColors: Record<string, string> = {
    primary: '#3b82f6', secondary: '#10b981', emergency: '#ef4444', after_hours: '#f59e0b',
  };
  const typeLabels: Record<string, string> = {
    primary: 'Primary', secondary: 'Secondary', emergency: 'Emergency', after_hours: 'After Hours',
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div className="panel-section-label" style={{ marginBottom: 0 }}><span style={CONTACT_PILL}>Contacts</span></div>
        {!contactForm && !contactLimitReached && (
          <button
            className="btn btn-ghost btn-sm combo1"
            onClick={(e) => { e.stopPropagation(); setContactForm(EMPTY_CONTACT); setEditingContact(null); }}
            style={{ fontSize: '0.75rem' }}
          >
            <Plus size={12} className="btn-icon" /> Add Contact
          </button>
        )}
      </div>

      {contacts.length === 0 && !contactForm && (
        <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', margin: 0 }}>No additional contacts.</p>
      )}

      {contacts.map((c) => {
        const color = typeColors[c.contact_type] ?? '#6366f1';
        return (
          <div key={c.id} style={{
            padding: '10px 12px', borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)',
            marginBottom: 6, display: 'flex', flexDirection: 'column', gap: 4,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 600, fontSize: '0.875rem', flex: 1 }}>{c.name}</span>
              <span style={{
                padding: '1px 7px', borderRadius: 999, fontSize: '0.6875rem', fontWeight: 700,
                background: `${color}22`, color, border: `1px solid ${color}44`,
              }}>{typeLabels[c.contact_type] ?? c.contact_type}</span>
              <button
                type="button"
                className="btn btn-ghost btn-sm combo1"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditingContact(c); setContactForm({ name: c.name, title: c.title ?? '', phone: c.phone ?? '', email: c.email ?? '', contact_type: c.contact_type, notes: c.notes ?? '' }); }}
                style={{ padding: '2px 6px' }}
              ><Pencil size={12} className="btn-icon" /></button>
              <button
                className="btn btn-sm combo3-danger"
                onClick={(e) => { e.stopPropagation(); setDeleteContactId(c.id); }}
                style={{ padding: '2px 6px' }}
              ><Trash2 size={12} className="btn-icon" /></button>
            </div>
            {c.title && <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{c.title}</div>}
            {c.phone && (
              <div style={{ fontSize: '0.8rem', display: 'flex', gap: 5, alignItems: 'center', color: 'var(--text-secondary)' }}>
                <Phone size={11} style={{ color: 'var(--text-muted)' }} />
                <a href={`tel:${c.phone}`} style={{ color: 'inherit' }}>{c.phone}</a>
              </div>
            )}
            {c.email && (
              <div style={{ fontSize: '0.8rem', display: 'flex', gap: 5, alignItems: 'center', color: 'var(--text-secondary)' }}>
                <Mail size={11} style={{ color: 'var(--text-muted)' }} />
                <a href={`mailto:${c.email}`} style={{ color: 'inherit' }}>{c.email}</a>
              </div>
            )}
            {c.notes && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: 2 }}>{c.notes}</div>}
          </div>
        );
      })}

      {contactLimitReached && !contactForm && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: 4 }}>
          <Lock size={12} style={{ flexShrink: 0 }} />
          One contact per vendor — upgrade to Pro for multiple contacts.
        </div>
      )}

      {deleteContactId !== null && (
        <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)', marginBottom: 6 }}>
          <p style={{ margin: '0 0 8px', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>Delete this contact?</p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); setDeleteContactId(null); }}>Cancel</button>
            <button className="btn btn-danger btn-sm" onClick={(e) => { e.stopPropagation(); handleDeleteContact(deleteContactId); }}>Delete</button>
          </div>
        </div>
      )}

      {contactForm && (
        <div onClick={(e) => e.stopPropagation()} style={{ padding: '12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-default)', background: 'var(--bg-base)', display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Name *</label>
              <input className="form-input" style={{ fontSize: '0.8125rem' }} value={contactForm.name} onChange={(e) => setContactForm((f) => f && ({ ...f, name: e.target.value }))} placeholder="Full name" autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Title</label>
              <input className="form-input" style={{ fontSize: '0.8125rem' }} value={contactForm.title} onChange={(e) => setContactForm((f) => f && ({ ...f, title: e.target.value }))} placeholder="Role or title" />
            </div>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Type</label>
              <select className="form-input" style={{ fontSize: '0.8125rem' }} value={contactForm.contact_type} onChange={(e) => setContactForm((f) => f && ({ ...f, contact_type: e.target.value as ContactForm['contact_type'] }))}>
                <option value="primary">Primary</option>
                <option value="secondary">Secondary</option>
                <option value="emergency">Emergency</option>
                <option value="after_hours">After Hours</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Phone</label>
              <input className="form-input" style={{ fontSize: '0.8125rem' }} value={contactForm.phone} onChange={(e) => setContactForm((f) => f && ({ ...f, phone: e.target.value }))} placeholder="(555) 000-0000" />
            </div>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Email</label>
              <input className="form-input" type="email" style={{ fontSize: '0.8125rem' }} value={contactForm.email} onChange={(e) => setContactForm((f) => f && ({ ...f, email: e.target.value }))} placeholder="email@vendor.com" />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Notes</label>
              <textarea className="form-textarea" style={{ fontSize: '0.8125rem' }} rows={2} value={contactForm.notes} onChange={(e) => setContactForm((f) => f && ({ ...f, notes: e.target.value }))} placeholder="Optional notes…" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); setContactForm(null); setEditingContact(null); }}>Cancel</button>
            <button className="btn btn-primary btn-sm" disabled={contactSaving || !contactForm.name.trim()} onClick={(e) => { e.stopPropagation(); handleSaveContact(); }}>
              {contactSaving ? 'Saving…' : editingContact ? 'Save Changes' : 'Add Contact'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Vendor Spend Section ─────────────────────────────────────────────────────

function VendorSpendSection({ vendorId, isManagerOrAdmin }: { vendorId: number; isManagerOrAdmin: boolean }) {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState<SpendEntry | null>(null);
  const [spendForm, setSpendForm] = useState({ service_date: '', amount: '', description: '', invoice_number: '' });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const { data, isLoading } = useQuery<SpendTotals>({
    queryKey: ['vendor-spend', vendorId],
    queryFn: async () => {
      const res = await api.get<SpendTotals>(`/vendors/${vendorId}/spend`);
      return res.data;
    },
    enabled: !!vendorId,
  });

  function openAdd() {
    setEditingEntry(null);
    setSpendForm({ service_date: '', amount: '', description: '', invoice_number: '' });
    setShowForm(true);
  }

  function openEdit(entry: SpendEntry) {
    setEditingEntry(entry);
    setSpendForm({
      service_date: entry.service_date.slice(0, 10),
      amount: entry.amount,
      description: entry.description ?? '',
      invoice_number: entry.invoice_number ?? '',
    });
    setShowForm(true);
  }

  async function handleSave() {
    if (!spendForm.service_date || !spendForm.amount) return;
    setSaving(true);
    try {
      const payload = {
        service_date: spendForm.service_date,
        amount: parseFloat(spendForm.amount),
        description: spendForm.description || null,
        invoice_number: spendForm.invoice_number || null,
      };
      if (editingEntry) {
        await api.put(`/vendors/${vendorId}/spend/${editingEntry.id}`, payload);
      } else {
        await api.post(`/vendors/${vendorId}/spend`, payload);
      }
      qc.invalidateQueries({ queryKey: ['vendor-spend', vendorId] });
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(entryId: number) {
    setDeletingId(entryId);
    try {
      await api.delete(`/vendors/${vendorId}/spend/${entryId}`);
      qc.invalidateQueries({ queryKey: ['vendor-spend', vendorId] });
    } finally {
      setDeletingId(null);
    }
  }

  const entries = data?.data ?? [];
  const ytdTotal = data?.ytd_total ?? 0;
  const currentYear = new Date().getFullYear();

  return (
    <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* YTD Summary */}
      <div style={{
        background: 'rgba(255,255,255,0.04)', borderRadius: 10,
        border: '1px solid var(--border-subtle)', padding: '14px 18px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 500 }}>{currentYear} YTD Spend</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#e2e8f0' }}>
            ${ytdTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
        {isManagerOrAdmin && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={openAdd}
            style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}
          >
            <Plus size={14} /> Log Expense
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div style={{
          background: 'rgba(255,255,255,0.04)', borderRadius: 10,
          border: '1px solid var(--border-subtle)', padding: '16px',
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
            {editingEntry ? 'Edit Expense' : 'Log Expense'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Service Date *</label>
              <DatePicker
                value={spendForm.service_date}
                onChange={v => setSpendForm(f => ({ ...f, service_date: v }))}
                placeholder="Select a date"
              />
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Amount ($) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="form-control"
                placeholder="0.00"
                value={spendForm.amount}
                onChange={e => setSpendForm(f => ({ ...f, amount: e.target.value }))}
              />
            </div>
          </div>
          <div>
            <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Description</label>
            <input
              type="text"
              className="form-control"
              placeholder="What was this service for?"
              value={spendForm.description}
              onChange={e => setSpendForm(f => ({ ...f, description: e.target.value }))}
            />
          </div>
          <div>
            <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Invoice #</label>
            <input
              type="text"
              className="form-control"
              placeholder="Optional"
              value={spendForm.invoice_number}
              onChange={e => setSpendForm(f => ({ ...f, invoice_number: e.target.value }))}
            />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleSave}
              disabled={saving || !spendForm.service_date || !spendForm.amount}
              style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}
            >
              {saving ? 'Saving…' : editingEntry ? 'Update' : 'Save'}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setShowForm(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Entries List */}
      {isLoading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem', textAlign: 'center', padding: '16px 0' }}>Loading…</div>
      ) : entries.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', textAlign: 'center', padding: '16px 0' }}>
          No expenses logged yet.{isManagerOrAdmin ? ' Click "Log Expense" to add one.' : ''}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {entries.map(entry => (
            <div
              key={entry.id}
              style={{
                background: 'rgba(255,255,255,0.03)', borderRadius: 8,
                border: '1px solid var(--border-subtle)', padding: '10px 14px',
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, color: '#e2e8f0', fontSize: '0.9rem' }}>
                    ${parseFloat(entry.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    {new Date(entry.service_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })}
                  </span>
                  {entry.invoice_number && (
                    <span style={{
                      fontSize: '0.7rem', background: 'rgba(99,102,241,0.15)', color: '#a5b4fc',
                      border: '1px solid rgba(99,102,241,0.3)', padding: '1px 6px', borderRadius: 99,
                    }}>
                      #{entry.invoice_number}
                    </span>
                  )}
                </div>
                {entry.description && (
                  <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginTop: 3 }}>{entry.description}</div>
                )}
                {entry.created_by_name && (
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>by {entry.created_by_name}</div>
                )}
              </div>
              {isManagerOrAdmin && (
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={() => openEdit(entry)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '2px 4px' }}
                    title="Edit"
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(entry.id)}
                    disabled={deletingId === entry.id}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '2px 4px', opacity: deletingId === entry.id ? 0.5 : 1 }}
                    title="Delete"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Vendor Documents Section ─────────────────────────────────────────────────

function docFileIcon(fileName: string): { color: string; label: string } {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'pdf') return { color: '#ef4444', label: 'PDF' };
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'avif'].includes(ext)) return { color: '#3b82f6', label: 'IMG' };
  if (['doc', 'docx'].includes(ext)) return { color: '#8b5cf6', label: 'DOC' };
  return { color: '#64748b', label: ext.toUpperCase() || 'FILE' };
}

function VendorDocumentsSection({ vendorId, isManagerOrAdmin }: { vendorId: number; isManagerOrAdmin: boolean }) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingDoc, setEditingDoc] = useState<VendorDocument | null>(null);
  const [docForm, setDocForm] = useState({ doc_type: 'COI', expiry_date: '', notes: '' });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const { data: docsRes, isLoading } = useQuery<VendorDocument[]>({
    queryKey: ['vendor-docs', vendorId],
    queryFn: async () => {
      const res = await api.get<VendorDocument[]>(`/vendors/${vendorId}/documents`);
      return res.data;
    },
    enabled: !!vendorId,
  });

  const docs = docsRes ?? [];

  // Group docs by doc_type
  const grouped = useMemo(() => {
    const map = new Map<string, VendorDocument[]>();
    docs.forEach((d) => {
      if (!map.has(d.doc_type)) map.set(d.doc_type, []);
      map.get(d.doc_type)!.push(d);
    });
    return map;
  }, [docs]);

  // Expiring within 30 days
  const expiringDocs = docs.filter(
    (d) => d.days_until_expiry !== null && d.days_until_expiry >= 0 && d.days_until_expiry <= 30
  );

  function openAdd() {
    setEditingDoc(null);
    setDocForm({ doc_type: 'COI', expiry_date: '', notes: '' });
    setSelectedFile(null);
    setShowForm(true);
  }

  function openEdit(doc: VendorDocument) {
    setEditingDoc(doc);
    setDocForm({
      doc_type: doc.doc_type,
      expiry_date: doc.expiry_date ? doc.expiry_date.slice(0, 10) : '',
      notes: doc.notes ?? '',
    });
    setSelectedFile(null);
    setShowForm(true);
  }

  async function handleUpload() {
    if (!selectedFile || !docForm.doc_type) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('doc_type', docForm.doc_type);
      if (docForm.expiry_date) formData.append('expiry_date', docForm.expiry_date);
      if (docForm.notes) formData.append('notes', docForm.notes);

      const baseUrl = import.meta.env.VITE_API_URL ?? '';
      const token = localStorage.getItem('accessToken');
      const resp = await fetch(`${baseUrl}/vendors/${vendorId}/documents`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      const result = await resp.json();
      if (result.success) {
        qc.invalidateQueries({ queryKey: ['vendor-docs', vendorId] });
        qc.invalidateQueries({ queryKey: ['vendor-expiring-docs'] });
        setShowForm(false);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    } finally {
      setUploading(false);
    }
  }

  async function handleUpdateMeta() {
    if (!editingDoc || !docForm.doc_type) return;
    setSaving(true);
    try {
      await api.put(`/vendors/${vendorId}/documents/${editingDoc.id}`, {
        doc_type: docForm.doc_type,
        expiry_date: docForm.expiry_date || null,
        notes: docForm.notes || null,
      });
      qc.invalidateQueries({ queryKey: ['vendor-docs', vendorId] });
      qc.invalidateQueries({ queryKey: ['vendor-expiring-docs'] });
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(docId: number) {
    setDeletingId(docId);
    try {
      await api.delete(`/vendors/${vendorId}/documents/${docId}`);
      qc.invalidateQueries({ queryKey: ['vendor-docs', vendorId] });
      qc.invalidateQueries({ queryKey: ['vendor-expiring-docs'] });
    } finally {
      setDeletingId(null);
    }
  }

  function expiryColor(days: number | null): string {
    if (days === null) return 'var(--text-muted)';
    if (days < 0) return '#ef4444';
    if (days <= 30) return '#f59e0b';
    return '#10b981';
  }

  function formatExpiry(doc: VendorDocument): string {
    if (!doc.expiry_date) return '';
    const d = new Date(doc.expiry_date);
    return `Expires ${d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit', timeZone: 'UTC' })}`;
  }

  return (
    <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Expiry Alert Banner */}
      {expiringDocs.length > 0 && (
        <div style={{
          background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)',
          borderRadius: 8, padding: '10px 14px',
          display: 'flex', alignItems: 'flex-start', gap: 8,
        }}>
          <AlertTriangle size={14} color="#f59e0b" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: '0.8125rem' }}>
            <span style={{ color: '#f59e0b', fontWeight: 600 }}>
              ⚠ {expiringDocs.length} document{expiringDocs.length !== 1 ? 's' : ''} expiring within 30 days:
            </span>{' '}
            <span style={{ color: 'var(--text-secondary)' }}>
              {expiringDocs.map((d) => d.file_name).join(', ')}
            </span>
          </div>
        </div>
      )}

      {/* Header row with upload button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
          {docs.length} document{docs.length !== 1 ? 's' : ''}
        </span>
        {isManagerOrAdmin && !showForm && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={openAdd}
            style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}
          >
            <Upload size={13} /> Upload
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div style={{
          background: 'rgba(255,255,255,0.04)', borderRadius: 10,
          border: '1px solid var(--border-subtle)', padding: '16px',
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
            {editingDoc ? 'Edit Document' : 'Upload Document'}
          </div>

          {/* Doc type select */}
          <div>
            <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Document Type *</label>
            <select
              className="form-control"
              value={docForm.doc_type}
              onChange={(e) => setDocForm((f) => ({ ...f, doc_type: e.target.value }))}
              style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)', borderRadius: 6, padding: '7px 10px', fontSize: '0.875rem', width: '100%' }}
            >
              {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {/* File picker (only for new upload) */}
          {!editingDoc && (
            <div>
              <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>File *</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="*/*"
                onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
                style={{ fontSize: '0.8125rem', color: 'var(--text-primary)' }}
              />
              {selectedFile && (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>{selectedFile.name}</div>
              )}
            </div>
          )}

          {/* Expiry date */}
          <div>
            <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Expiry Date (optional)</label>
            <DatePicker
              value={docForm.expiry_date}
              onChange={(v) => setDocForm((f) => ({ ...f, expiry_date: v }))}
              placeholder="Select a date"
            />
          </div>

          {/* Notes */}
          <div>
            <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Notes (optional)</label>
            <textarea
              className="form-control"
              rows={2}
              placeholder="Any notes about this document…"
              value={docForm.notes}
              onChange={(e) => setDocForm((f) => ({ ...f, notes: e.target.value }))}
              style={{ resize: 'vertical', minHeight: 54 }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            {editingDoc ? (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleUpdateMeta}
                disabled={saving}
                style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}
              >
                {saving ? 'Saving…' : 'Update'}
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleUpload}
                disabled={uploading || !selectedFile}
                style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}
              >
                {uploading ? (
                  <><span className="loading-spinner" style={{ width: 12, height: 12, borderWidth: 2 }} /> Uploading…</>
                ) : (
                  <><Upload size={13} /> Upload Document</>
                )}
              </button>
            )}
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => { setShowForm(false); setSelectedFile(null); }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Document List */}
      {isLoading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem', textAlign: 'center', padding: '16px 0' }}>Loading…</div>
      ) : docs.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', textAlign: 'center', padding: '16px 0' }}>
          No documents uploaded yet.{isManagerOrAdmin ? ' Click "Upload" to add one.' : ''}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {Array.from(grouped.entries()).map(([type, typeDocs]) => (
            <div key={type}>
              <div style={{
                fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 6,
              }}>
                {type}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {typeDocs.map((doc) => {
                  const { color: iconColor, label: iconLabel } = docFileIcon(doc.file_name);
                  const nameDisplay = doc.file_name.length > 35 ? doc.file_name.slice(0, 32) + '…' : doc.file_name;
                  return (
                    <div
                      key={doc.id}
                      style={{
                        background: 'rgba(255,255,255,0.03)', borderRadius: 8,
                        border: '1px solid var(--border-subtle)', padding: '9px 12px',
                        display: 'flex', alignItems: 'center', gap: 10,
                      }}
                    >
                      {/* File icon */}
                      <div style={{
                        width: 30, height: 30, borderRadius: 6, flexShrink: 0,
                        background: `${iconColor}20`, border: `1px solid ${iconColor}40`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.5rem', fontWeight: 700, color: iconColor,
                      }}>
                        {iconLabel}
                      </div>

                      {/* File info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--text-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {nameDisplay}
                        </div>
                        {doc.expiry_date && (
                          <div style={{ fontSize: '0.7rem', color: expiryColor(doc.days_until_expiry), marginTop: 2 }}>
                            {formatExpiry(doc)}
                            {doc.days_until_expiry !== null && doc.days_until_expiry < 0
                              ? ' (expired)'
                              : doc.days_until_expiry !== null && doc.days_until_expiry <= 30
                              ? ` (${doc.days_until_expiry}d)`
                              : ''}
                          </div>
                        )}
                        {!doc.expiry_date && (
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>No expiry set</div>
                        )}
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0, alignItems: 'center' }}>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', padding: '3px 8px' }}
                          onClick={(e) => { e.stopPropagation(); openVendorFile(doc.file_url, doc.file_name); }}
                        >
                          <Eye size={12} /> View
                        </button>
                        {isManagerOrAdmin && (
                          <>
                            <button
                              type="button"
                              onClick={() => openEdit(doc)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '3px 4px' }}
                              title="Edit metadata"
                            >
                              <Pencil size={12} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(doc.id)}
                              disabled={deletingId === doc.id}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '3px 4px', opacity: deletingId === doc.id ? 0.5 : 1 }}
                              title="Delete document"
                            >
                              <Trash2 size={12} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Star Picker (inline 1–5 star selector) ──────────────────────────────────

function StarPicker({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          onClick={() => onChange(i)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 20, color: i <= value ? '#f59e0b' : 'rgba(255,255,255,0.15)',
            padding: '2px 1px', lineHeight: 1, transition: 'color 100ms ease',
          }}
        >
          ★
        </button>
      ))}
    </div>
  );
}

// ─── Vendor Ratings Section ───────────────────────────────────────────────────

function VendorRatingsSection({ vendorId, isManagerOrAdmin }: { vendorId: number; isManagerOrAdmin: boolean }) {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [ratingForm, setRatingForm] = useState({
    service_date: '',
    response_time: 5 as number,
    work_quality: 5 as number,
    communication: 5 as number,
    pricing: 5 as number,
    reliability: 5 as number,
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const { data: ratingsData, isLoading } = useQuery<RatingsData>({
    queryKey: ['vendor-ratings', vendorId],
    queryFn: async () => {
      const res = await api.get<RatingsData>(`/vendors/${vendorId}/ratings`);
      return res.data;
    },
    enabled: !!vendorId,
  });

  const ratings = ratingsData?.ratings ?? [];
  const averages = ratingsData?.averages;
  const totalRatings = Number(averages?.total_ratings ?? 0);
  const avgOverall = Number(averages?.avg_overall ?? 0);

  function resetForm() {
    setRatingForm({ service_date: '', response_time: 5, work_quality: 5, communication: 5, pricing: 5, reliability: 5, notes: '' });
  }

  async function handleSave() {
    if (!ratingForm.service_date) return;
    setSaving(true);
    try {
      await api.post(`/vendors/${vendorId}/ratings`, {
        service_date: ratingForm.service_date,
        response_time: ratingForm.response_time,
        work_quality: ratingForm.work_quality,
        communication: ratingForm.communication,
        pricing: ratingForm.pricing,
        reliability: ratingForm.reliability,
        notes: ratingForm.notes || null,
      });
      qc.invalidateQueries({ queryKey: ['vendor-ratings', vendorId] });
      qc.invalidateQueries({ queryKey: ['vendors'] });
      setShowForm(false);
      resetForm();
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(ratingId: number) {
    setDeletingId(ratingId);
    try {
      await api.delete(`/vendors/${vendorId}/ratings/${ratingId}`);
      qc.invalidateQueries({ queryKey: ['vendor-ratings', vendorId] });
      qc.invalidateQueries({ queryKey: ['vendors'] });
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Average Score Panel */}
      {totalRatings > 0 && averages && (
        <div style={{
          background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)',
          borderRadius: 12, padding: '16px 20px',
          display: 'flex', gap: 20, alignItems: 'flex-start',
        }}>
          {/* Big number + stars */}
          <div style={{ textAlign: 'center', minWidth: 72 }}>
            <div style={{ fontSize: '2.5rem', fontWeight: 700, color: '#f59e0b', lineHeight: 1 }}>
              {avgOverall.toFixed(1)}
            </div>
            <div style={{ display: 'flex', gap: 1, justifyContent: 'center', marginTop: 6 }}>
              {[1, 2, 3, 4, 5].map((i) => {
                const full = avgOverall >= i - 0.25;
                const half = !full && avgOverall >= i - 0.75;
                if (full) return <span key={i} style={{ fontSize: 14, color: '#f59e0b' }}>★</span>;
                if (half) return (
                  <span key={i} style={{ position: 'relative', display: 'inline-block', fontSize: 14, color: 'rgba(255,255,255,0.2)' }}>
                    ★<span style={{ position: 'absolute', left: 0, top: 0, width: '50%', overflow: 'hidden', color: '#f59e0b' }}>★</span>
                  </span>
                );
                return <span key={i} style={{ fontSize: 14, color: 'rgba(255,255,255,0.2)' }}>★</span>;
              })}
            </div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 4 }}>
              {totalRatings} review{totalRatings !== 1 ? 's' : ''}
            </div>
          </div>
          {/* Criteria bars */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {RATING_CRITERIA.map((c) => {
              const val = Number((averages as any)[`avg_${c.key}`] ?? 0);
              return (
                <div key={c.key}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{c.icon} {c.label}</span>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#f59e0b' }}>{val.toFixed(1)}</span>
                  </div>
                  <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 4, height: 5 }}>
                    <div style={{
                      width: `${(val / 5) * 100}%`, height: '100%',
                      background: 'linear-gradient(90deg, #f59e0b, #fbbf24)',
                      borderRadius: 4, transition: 'width 0.4s ease',
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add Rating button */}
      {isManagerOrAdmin && !showForm && (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => { resetForm(); setShowForm(true); }}
          style={{ alignSelf: 'flex-start', display: 'inline-flex', gap: 6, alignItems: 'center' }}
        >
          <Plus size={14} /> Add Rating
        </button>
      )}

      {/* Add Rating form */}
      {isManagerOrAdmin && showForm && (
        <div style={{
          background: 'rgba(255,255,255,0.04)', borderRadius: 10,
          border: '1px solid var(--border-subtle)', padding: '16px',
          display: 'flex', flexDirection: 'column', gap: 12,
        }}>
          <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>Add Rating</div>

          <div>
            <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Service Date *</label>
            <DatePicker
              value={ratingForm.service_date}
              onChange={(v) => setRatingForm((f) => ({ ...f, service_date: v }))}
              placeholder="Select a date"
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {RATING_CRITERIA.map((c) => (
              <div key={c.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', minWidth: 130 }}>
                  {c.icon} {c.label}
                </span>
                <StarPicker
                  value={ratingForm[c.key]}
                  onChange={(v) => setRatingForm((f) => ({ ...f, [c.key]: v }))}
                />
              </div>
            ))}
          </div>

          <div>
            <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Notes (optional)</label>
            <textarea
              className="form-control"
              rows={2}
              placeholder="Any notes about this service…"
              value={ratingForm.notes}
              onChange={(e) => setRatingForm((f) => ({ ...f, notes: e.target.value }))}
              style={{ resize: 'vertical', minHeight: 54 }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleSave}
              disabled={saving || !ratingForm.service_date}
              style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}
            >
              {saving ? 'Saving…' : 'Submit Rating'}
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowForm(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Ratings list */}
      {isLoading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem', textAlign: 'center', padding: '16px 0' }}>Loading…</div>
      ) : ratings.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', textAlign: 'center', padding: '16px 0' }}>
          No ratings yet.{isManagerOrAdmin ? ' Click "Add Rating" to submit one.' : ''}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {ratings.map((r) => {
            const overallScore = (r.response_time + r.work_quality + r.communication + r.pricing + r.reliability) / 5;
            return (
              <div key={r.id} style={{
                background: 'rgba(255,255,255,0.03)', borderRadius: 8,
                border: '1px solid var(--border-subtle)', padding: '12px 14px',
                display: 'flex', flexDirection: 'column', gap: 8,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: '1rem', fontWeight: 700, color: '#f59e0b' }}>{overallScore.toFixed(1)}</span>
                    <div style={{ display: 'flex', gap: 1 }}>
                      {[1, 2, 3, 4, 5].map((i) => (
                        <span key={i} style={{ fontSize: 11, color: i <= Math.round(overallScore) ? '#f59e0b' : 'rgba(255,255,255,0.15)' }}>★</span>
                      ))}
                    </div>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {new Date(r.service_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })}
                    </span>
                  </div>
                  {isManagerOrAdmin && (
                    <button
                      type="button"
                      onClick={() => handleDelete(r.id)}
                      disabled={deletingId === r.id}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(239,68,68,0.6)', padding: '2px 4px', opacity: deletingId === r.id ? 0.4 : 1 }}
                      title="Delete rating"
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {RATING_CRITERIA.map((c) => {
                    const score = r[c.key];
                    const color = score >= 4 ? '#10b981' : score >= 3 ? '#f59e0b' : '#ef4444';
                    return (
                      <span key={c.key} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 3,
                        fontSize: '0.6875rem', fontWeight: 600, padding: '2px 7px', borderRadius: 99,
                        background: `${color}18`, color, border: `1px solid ${color}40`,
                      }}>
                        {c.icon} {score}/5
                      </span>
                    );
                  })}
                </div>
                {r.notes && (
                  <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', fontStyle: 'italic', lineHeight: 1.4 }}>
                    "{r.notes}"
                  </div>
                )}
                {r.rated_by_name && (
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>by {r.rated_by_name}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Vendor Detail Slide-Out Panel ────────────────────────────────────────────

interface PanelProps {
  vendor: Vendor | null;
  catMap: Map<string, ServiceCategory>;
  isPro: boolean;
  isEnterprise: boolean;
  isManagerOrAdmin: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onLogoUploaded: (updated: Vendor) => void;
}

type ContactForm = {
  name: string; title: string; phone: string; email: string;
  contact_type: 'primary' | 'secondary' | 'emergency' | 'after_hours'; notes: string;
};

const EMPTY_CONTACT: ContactForm = { name: '', title: '', phone: '', email: '', contact_type: 'primary', notes: '' };

function VendorDetailPanel({ vendor, catMap, isPro, isEnterprise, isManagerOrAdmin, onClose, onEdit, onDelete, onLogoUploaded }: PanelProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [logoMode, setLogoMode] = useState<'file' | 'url'>('file');
  const [urlInput, setUrlInput] = useState('');
  const [urlSaving, setUrlSaving] = useState(false);
  const [removingLogo, setRemovingLogo] = useState(false);
  const [activeTab, setActiveTab] = useState<'contacts' | 'contracts' | 'spend' | 'documents' | 'ratings'>('contacts');
  const [showAiExtract, setShowAiExtract] = useState(false);
  const [extractText, setExtractText] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [extractResult, setExtractResult] = useState<{
    name: string | null;
    phone: string | null;
    email: string | null;
    website: string | null;
    address: string | null;
    category: string | null;
    notes: string | null;
    logo_url: string | null;
    hours_of_operation: string | null;
  } | null>(null);
  const [applyChoices, setApplyChoices] = useState<Record<string, 'replace' | 'add' | 'skip'>>({});
  const [applying, setApplying] = useState(false);
  const [applySuccess, setApplySuccess] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const isOpen = vendor !== null;

  // Floating logo sidebar needs room to the left of the panel — hide below this viewport width
  const FLOATING_LOGO_MIN_WIDTH = 730;
  const [showFloatingLogo, setShowFloatingLogo] = useState(() => window.innerWidth >= FLOATING_LOGO_MIN_WIDTH);
  useEffect(() => {
    const handler = () => setShowFloatingLogo(window.innerWidth >= FLOATING_LOGO_MIN_WIDTH);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Lock body scroll when open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Reset tab and AI extract state when vendor changes
  useEffect(() => {
    setActiveTab('contacts');
    setUrlInput(vendor?.logo_url || '');
    setShowAiExtract(false);
    setExtractText('');
    setExtractResult(null);
    setApplyChoices({});
    setApplySuccess(false);
    setApplyError(null);
  }, [vendor?.id]);

  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !vendor) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const res = await apiClient.post<{ success: boolean; data: Vendor }>(
        `/vendors/${vendor.id}/logo`,
        fd
      );
      if (res.data.success) onLogoUploaded(res.data.data);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleSaveUrl() {
    if (!urlInput.trim() || !vendor) return;
    setUrlSaving(true);
    try {
      const res = await apiClient.patch<{ success: boolean; data: Vendor }>(`/vendors/${vendor.id}/logo-url`, { url: urlInput.trim() });
      if (res.data.success) onLogoUploaded(res.data.data);
    } finally {
      setUrlSaving(false);
    }
  }

  async function handleRemoveLogo() {
    if (!vendor) return;
    setRemovingLogo(true);
    try {
      const res = await apiClient.delete<{ success: boolean; data: Vendor }>(`/vendors/${vendor.id}/logo`);
      if (res.data.success) onLogoUploaded(res.data.data);
    } finally {
      setRemovingLogo(false);
    }
  }

  async function extractVendorInfo() {
    if (!vendor) return;
    setExtracting(true);
    setExtractResult(null);
    setApplyChoices({});
    try {
      const apiUrl = import.meta.env.VITE_API_URL;
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${apiUrl}/vendors/ai-extract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: extractText.trim() || undefined, vendorName: vendor.name, vendorWebsite: vendor.website || undefined }),
      });
      const data = await res.json();
      if (data.success && data.data) {
        setExtractResult(data.data);
        const defaults: Record<string, 'replace' | 'add' | 'skip'> = {};
        Object.entries(data.data).forEach(([k, v]) => {
          defaults[k] = v ? 'replace' : 'skip';
        });
        setApplyChoices(defaults);
      }
    } catch (e) {
      console.error('AI extract failed', e);
    } finally {
      setExtracting(false);
    }
  }

  async function applyExtracted() {
    if (!extractResult || !vendor) return;
    setApplying(true);
    setApplyError(null);
    try {
      const patch: Record<string, string> = {};

      if (applyChoices.name === 'replace' && extractResult.name)
        patch.name = extractResult.name;
      if (applyChoices.website === 'replace' && extractResult.website)
        patch.website = extractResult.website;
      if (applyChoices.address === 'replace' && extractResult.address)
        patch.address = extractResult.address;
      if (applyChoices.hours_of_operation === 'replace' && extractResult.hours_of_operation)
        patch.hours_of_operation = extractResult.hours_of_operation;
      if (applyChoices.notes === 'replace' && extractResult.notes)
        patch.notes = extractResult.notes;
      else if (applyChoices.notes === 'add' && extractResult.notes)
        patch.notes = vendor.notes
          ? vendor.notes + '\n\n' + extractResult.notes
          : extractResult.notes;
      if (applyChoices.phone === 'replace' && extractResult.phone)
        patch.vendor_phone = extractResult.phone;
      if (applyChoices.email === 'replace' && extractResult.email)
        patch.vendor_email = extractResult.email;

      if (Object.keys(patch).length > 0) {
        const res = await apiClient.patch(`/vendors/${vendor.id}/partial`, patch);
        if (!res.data.success) throw new Error('Partial update failed');
      }

      if (applyChoices.phone === 'add' && extractResult.phone) {
        await apiClient.post(`/vendors/${vendor.id}/contacts`, {
          contact_type: 'primary',
          name: extractResult.name || vendor.name,
          phone: extractResult.phone,
          email: applyChoices.email === 'add' ? extractResult.email : null,
        });
      }

      if (applyChoices.email === 'add' && extractResult.email && applyChoices.phone !== 'add') {
        await apiClient.post(`/vendors/${vendor.id}/contacts`, {
          contact_type: 'primary',
          name: extractResult.name || vendor.name,
          phone: null,
          email: extractResult.email,
        });
      }

      if (applyChoices.logo_url === 'replace' && extractResult.logo_url && !vendor.logo_url) {
        await apiClient.patch(`/vendors/${vendor.id}/logo-url`, { url: extractResult.logo_url });
      }

      await queryClient.invalidateQueries({ queryKey: ['vendors'] });
      setApplySuccess(true);
    } catch (e) {
      console.error('Apply failed', e);
      setApplyError('Something went wrong applying changes. Please try again.');
    } finally {
      setApplying(false);
    }
  }

  const phone = vendor ? (vendor.vendor_phone ?? vendor.contact_phone) : null;
  const email = vendor ? (vendor.vendor_email ?? vendor.contact_email) : null;
  const websiteUrl = vendor ? safeWebsiteUrl(vendor.website) : null;

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 998,
          background: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          transition: 'opacity 0.25s ease',
        }}
      />

      {/* Floating Logo — sits on the backdrop, left of the panel. Hidden on narrow viewports. */}
      {isOpen && vendor && showFloatingLogo && (
        <div
          style={{
            position: 'fixed',
            top: 100,
            right: 'calc(min(720px, 90vw) + 24px)',
            width: 200,
            zIndex: 999,
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-default)',
            borderRadius: 12,
            padding: 16,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
            opacity: isOpen ? 1 : 0,
            pointerEvents: isOpen ? 'auto' : 'none',
            transition: 'opacity 0.25s ease',
          }}
        >
          {/* Logo preview — only show when logo exists */}
          {vendor.logo_url && (
            <>
              <div
                className="vendor-logo-wrapper"
                style={{
                  width: 180, height: 180, aspectRatio: '1', borderRadius: 20,
                  border: '2px solid var(--border-default)',
                  background: 'rgba(128,128,128,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                <img
                  src={getImageSrc(vendor.logo_url) ?? undefined}
                  alt={`${vendor.name} logo`}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              </div>
              <button
                type="button"
                className="combo3-danger"
                onClick={handleRemoveLogo}
                disabled={removingLogo}
                style={{
                  background: 'transparent',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  padding: '3px 10px',
                }}
              >
                {removingLogo ? 'Removing…' : '✕ Remove logo'}
              </button>
            </>
          )}

          {/* Mode toggle */}
          <div style={{ display: 'flex', gap: 2, background: 'var(--bg-elevated)', borderRadius: 8, padding: 2 }}>
            {(['file', 'url'] as const).map((m) => (
              <button
                key={m}
                type="button"
                className="combo1-icon"
                onClick={() => setLogoMode(m)}
                style={{
                  padding: '4px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                  fontSize: '0.8rem', fontWeight: 500,
                  background: logoMode === m ? 'var(--bg-elevated)' : 'transparent',
                  color: logoMode === m ? 'var(--text-primary)' : 'var(--text-muted)',
                  boxShadow: logoMode === m ? '0 1px 3px rgba(0,0,0,0.2)' : 'none',
                }}
              >
                {m === 'file' ? 'Upload File' : 'From URL'}
              </button>
            ))}
          </div>

          {/* File upload */}
          {logoMode === 'file' && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleLogoUpload}
              />
              <button
                className="btn btn-ghost btn-sm combo1-icon"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload size={13} className="btn-icon" />
                {uploading ? 'Uploading…' : vendor.logo_url ? 'Change Logo' : 'Upload Logo'}
              </button>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0, textAlign: 'center' }}>
                Any image format (PNG, JPG, WebP, SVG…) · Max 5MB
              </p>
            </>
          )}

          {/* URL input */}
          {logoMode === 'url' && (
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  className="form-input"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://example.com/logo.png"
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveUrl()}
                  style={{ flex: 1, fontSize: '0.8125rem' }}
                />
                <button
                  className="btn btn-primary btn-sm"
                  disabled={urlSaving || !urlInput.trim()}
                  onClick={handleSaveUrl}
                >
                  {urlSaving ? '…' : 'Save'}
                </button>
              </div>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0 }}>
                Paste any public image URL
              </p>
            </div>
          )}
        </div>
      )}

      {/* Slide Panel */}
      <div
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 999,
          width: 'min(720px, 90vw)',
          background: 'var(--bg-elevated)',
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          borderLeft: '1px solid var(--border-subtle)',
          boxShadow: '4px 0 24px rgba(0,0,0,0.5)',
          display: 'flex', flexDirection: 'column',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        {vendor && (
          <>
            {/* Fixed Header */}
            <div style={{ flexShrink: 0, padding: '20px 24px 16px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700, color: 'var(--text-primary)' }}>{vendor.name}</h2>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>Vendor Details</div>
                  {vendor.is_main_vendor && (
                    <span style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontSize: '0.7rem', padding: '2px 8px', borderRadius: 20, fontWeight: 600, marginTop: 6, display: 'inline-block' }}>
                      Main Vendor
                    </span>
                  )}
                </div>
                <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20, padding: 4 }}>×</button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 40 }}>

            {/* Vendor Info */}
            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 20 }}>

              {/* Service Tags */}
              {vendor.service_categories.length > 0 && (
                <div>
                  <div className="panel-section-label">Services</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {vendor.service_categories.map((cat) => (
                      <ServicePill key={cat} name={cat} category={catMap.get(cat)} />
                    ))}
                  </div>
                </div>
              )}

              {/* Vendor Contact */}
              {(phone || email || websiteUrl || vendor.hours_of_operation || vendor.account_number || vendor.address) && (
                <div>
                  <div className="panel-section-label"><span style={CONTACT_PILL}>Vendor Contact</span></div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                    {phone && (
                      <PanelRow icon={<Phone size={14} />}>
                        <a href={`tel:${phone}`} style={{ color: 'var(--text-secondary)' }}>{phone}</a>
                      </PanelRow>
                    )}
                    {email && (
                      <PanelRow icon={<Mail size={14} />}>
                        <a href={`mailto:${email}`} style={{ color: 'var(--text-secondary)' }}>{email}</a>
                      </PanelRow>
                    )}
                    {websiteUrl && (
                      <PanelRow icon={<Globe size={14} />}>
                        <a
                          href={websiteUrl}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: 'var(--accent-primary)', wordBreak: 'break-all' }}
                        >
                          {vendor.website?.replace(/^https?:\/\/(www\.)?/, '') ?? websiteUrl}
                        </a>
                      </PanelRow>
                    )}
                    {vendor.address && (
                      <PanelRow icon={<MapPin size={14} />}>
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(vendor.address)}`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}
                        >
                          {vendor.address}
                        </a>
                      </PanelRow>
                    )}
                    {vendor.address && (
                      <div style={{ marginTop: 8, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border-subtle)' }}>
                        <iframe
                          title="Vendor location"
                          width="100%"
                          height="140"
                          style={{ display: 'block', border: 'none' }}
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          src={`https://maps.google.com/maps?q=${encodeURIComponent(vendor.address)}&output=embed&z=14`}
                        />
                      </div>
                    )}
                    {vendor.hours_of_operation && !vendor.is_24_hour_service && (
                      <PanelRow icon={<Clock size={14} />}>
                        <span style={{ color: 'var(--text-secondary)' }}>{vendor.hours_of_operation}</span>
                      </PanelRow>
                    )}
                    {vendor.account_number && (
                      <PanelRow icon={<Hash size={14} />}>
                        <span style={{ color: 'var(--text-secondary)' }}>Acct: {vendor.account_number}</span>
                      </PanelRow>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              {vendor.notes && (
                <div>
                  <div className="panel-section-label">Notes</div>
                  <div style={{
                    marginTop: 8, fontSize: '0.875rem',
                    color: 'var(--text-secondary)', lineHeight: 1.6,
                    background: 'var(--bg-base)', borderRadius: 'var(--radius-sm)',
                    padding: '10px 12px', borderLeft: '3px solid var(--border-strong)',
                    whiteSpace: 'pre-wrap',
                  }}>
                    {vendor.notes}
                  </div>
                </div>
              )}

              {/* Contract Info (Pro/Enterprise) */}
              {isPro && (vendor.contract_start_date || vendor.contract_expiry_date || vendor.contract_notes) && (
                <div>
                  <div className="panel-section-label"><span style={CONTRACT_PILL}>Contract</span></div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    {vendor.contract_start_date && (
                      <PanelRow icon={<Calendar size={14} />}>
                        <span>Start: {fmtContractDate(vendor.contract_start_date)}</span>
                      </PanelRow>
                    )}
                    {vendor.contract_expiry_date && (() => {
                      const status = contractExpiryStatus(vendor.contract_expiry_date);
                      const colors = { ok: '#10b981', soon: '#f59e0b', expired: '#ef4444', null: 'var(--text-secondary)' };
                      return (
                        <PanelRow icon={<Calendar size={14} />}>
                          <span style={{ color: colors[status ?? 'null'] }}>
                            Expiry: {fmtContractDate(vendor.contract_expiry_date)}
                            {status === 'soon' && ' — Expires soon'}
                            {status === 'expired' && ' — Expired'}
                          </span>
                        </PanelRow>
                      );
                    })()}
                    {vendor.contract_notes && (
                      <div style={{
                        fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.5,
                        background: 'var(--bg-base)', borderRadius: 'var(--radius-sm)',
                        padding: '8px 10px', borderLeft: '3px solid var(--border-strong)', marginTop: 2,
                      }}>
                        {vendor.contract_notes}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tab Nav — pill style matching Inventory */}
              <hr className="divider" style={{ margin: '0' }} />
              <div style={{ padding: '12px 24px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 2, border: '0.5px solid var(--border-default)', borderRadius: 20, padding: 3, background: 'transparent' }}>
                  {[
                    { id: 'contacts' as const, label: 'Contacts', icon: <Users size={14} /> },
                    { id: 'contracts' as const, label: 'Contracts', icon: <FileText size={14} /> },
                    { id: 'spend' as const, label: 'Spend', icon: <DollarSign size={14} /> },
                    { id: 'documents' as const, label: 'Documents', icon: <Paperclip size={14} /> },
                    { id: 'ratings' as const, label: 'Ratings', icon: <Star size={14} /> },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '5px 9px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                        color: activeTab === tab.id ? 'var(--text-primary)' : 'var(--text-secondary)',
                        borderRadius: 20,
                        background: activeTab === tab.id ? 'var(--bg-active)' : 'transparent',
                        border: 'none', whiteSpace: 'nowrap',
                        transition: 'background 0.15s, color 0.15s',
                      }}
                    >
                      {tab.icon}{tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              {activeTab === 'contacts' && (
                <VendorContactsSection vendorId={vendor.id} isPro={isPro} />
              )}

              {activeTab === 'contracts' && (
                isPro ? (
                  <VendorContractsSection
                    vendorId={vendor.id}
                    isPro={isPro}
                    isEnterprise={isEnterprise}
                  />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '28px 24px', textAlign: 'center' }}>
                    <Lock size={20} color="#f59e0b" />
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.9rem' }}>Contract Tracking — Pro Feature</div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', maxWidth: 280, lineHeight: 1.5 }}>Track vendor contracts, renewal dates, and get AI contract analysis with Pro or Enterprise.</div>
                    <button type="button" className="btn btn-ghost btn-sm" style={{ color: '#f59e0b', borderColor: 'rgba(245,158,11,0.3)', marginTop: 4 }}>Upgrade to Pro</button>
                  </div>
                )
              )}

              {activeTab === 'spend' && (
                isPro ? (
                  <VendorSpendSection vendorId={vendor.id} isManagerOrAdmin={isManagerOrAdmin} />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '28px 24px', textAlign: 'center' }}>
                    <Lock size={20} color="#f59e0b" />
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.9rem' }}>Spend Tracking — Pro Feature</div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', maxWidth: 280, lineHeight: 1.5 }}>Log service call costs, track YTD spend, and manage vendor invoices with Pro or Enterprise.</div>
                    <button type="button" className="btn btn-ghost btn-sm" style={{ color: '#f59e0b', borderColor: 'rgba(245,158,11,0.3)', marginTop: 4 }}>Upgrade to Pro</button>
                  </div>
                )
              )}

              {activeTab === 'documents' && (
                isPro ? (
                  <VendorDocumentsSection vendorId={vendor.id} isManagerOrAdmin={isManagerOrAdmin} />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '28px 24px', textAlign: 'center' }}>
                    <Lock size={20} color="#f59e0b" />
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.9rem' }}>Document Storage — Pro Feature</div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', maxWidth: 280, lineHeight: 1.5 }}>Store COI, W9, licenses, and other vendor documents with expiry tracking and alerts — Pro or Enterprise.</div>
                    <button type="button" className="btn btn-ghost btn-sm" style={{ color: '#f59e0b', borderColor: 'rgba(245,158,11,0.3)', marginTop: 4 }}>Upgrade to Pro</button>
                  </div>
                )
              )}

              {activeTab === 'ratings' && (
                isPro ? (
                  <VendorRatingsSection vendorId={vendor.id} isManagerOrAdmin={isManagerOrAdmin} />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '28px 24px', textAlign: 'center' }}>
                    <Lock size={20} color="#f59e0b" />
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.9rem' }}>Performance Scorecard — Pro Feature</div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', maxWidth: 280, lineHeight: 1.5 }}>Rate vendors across 5 criteria, track performance over time, and sort by rating — Pro or Enterprise.</div>
                    <button type="button" className="btn btn-ghost btn-sm" style={{ color: '#f59e0b', borderColor: 'rgba(245,158,11,0.3)', marginTop: 4 }}>Upgrade to Pro</button>
                  </div>
                )
              )}

              {/* Date Added */}
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', paddingTop: 4 }}>
                Added {new Date(vendor.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 10, paddingTop: 8 }}>
                <button className="btn btn-ghost combo1" onClick={onEdit} style={{ display: 'inline-flex', width: 'auto', paddingLeft: 16, paddingRight: 16 }}>
                  <Pencil size={14} className="btn-icon" /> Edit Vendor
                </button>
                {(isPro || isEnterprise) && (
                  <button
                    type="button"
                    className="btn btn-sm combo4-ai"
                    onClick={() => {
                      if (!vendor) return;
                      const hints: string[] = [];
                      if (vendor.name)                hints.push(`Business name: ${vendor.name}`);
                      if (vendor.website)             hints.push(`Website: ${vendor.website}`);
                      if (vendor.vendor_phone)        hints.push(`Phone: ${vendor.vendor_phone}`);
                      if (vendor.contact_phone)       hints.push(`Phone: ${vendor.contact_phone}`);
                      if (vendor.vendor_email)        hints.push(`Email: ${vendor.vendor_email}`);
                      if (vendor.contact_email)       hints.push(`Email: ${vendor.contact_email}`);
                      if (vendor.address)             hints.push(`Address: ${vendor.address}`);
                      if (vendor.hours_of_operation)  hints.push(`Hours: ${vendor.hours_of_operation}`);
                      setExtractText(hints.join('\n'));
                      setShowAiExtract(true);
                    }}
                  >
                    <Sparkles size={13} className="btn-icon" /> AI Extract
                  </button>
                )}
                <button
                  className="btn combo3-danger"
                  onClick={onDelete}
                >
                  <Trash2 size={14} className="btn-icon" />
                </button>
              </div>
            </div>
            </div>{/* end scrollable content */}
          </>
        )}
      </div>

      {/* AI Extract Modal */}
      {showAiExtract && vendor && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 1001, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => { if (!extracting && !applying) setShowAiExtract(false); }}
        >
          <div
            style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 20, padding: 28, width: '100%', maxWidth: 560, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.6)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Sparkles size={16} className="modal-icon-intro" style={{ color: '#a78bfa' }} /> AI Profile Extraction
              </h3>
              <button className="modal-close" onClick={() => setShowAiExtract(false)}>
                <X size={18} className="btn-icon" />
              </button>
            </div>

            {!extractResult ? (
              <>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: 16, lineHeight: 1.5 }}>
                  Claude will search the web for <strong style={{ color: 'var(--text-primary)' }}>{vendor.name}</strong> and extract their contact details, address, hours, and more. Optionally paste extra context below to help narrow the search.
                </p>
                <textarea
                  value={extractText}
                  onChange={(e) => setExtractText(e.target.value)}
                  placeholder="Optional: paste extra context to help — e.g. city, state, website URL, or phone number hint..."
                  rows={6}
                  style={{ width: '100%', background: 'var(--bg-base)', border: '1px solid var(--border-default)', borderRadius: 8, padding: '10px 12px', color: 'var(--text-primary)', fontSize: '0.875rem', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                  <button type="button" className="btn btn-ghost combo-cancel-jello" onClick={() => setShowAiExtract(false)}>Cancel</button>
                  <button
                    type="button"
                    className="btn combo2"
                    disabled={extracting}
                    onClick={extractVendorInfo}
                    style={{ background: '#7c3aed', color: 'white', border: 'none', opacity: extracting ? 0.6 : 1 }}
                  >
                    {extracting ? 'Searching…' : '✦ Search & Extract'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: 16 }}>
                  Review extracted fields. Choose what to apply.
                </p>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <th style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>Field</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>Current</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>Suggested</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {([
                      { key: 'name', label: 'Name', current: vendor.name, suggested: extractResult.name, actions: ['replace', 'skip'] as const },
                      { key: 'phone', label: 'Phone', current: vendor.vendor_phone, suggested: extractResult.phone, actions: ['replace', 'add', 'skip'] as const },
                      { key: 'email', label: 'Email', current: vendor.vendor_email, suggested: extractResult.email, actions: ['replace', 'add', 'skip'] as const },
                      { key: 'website', label: 'Website', current: vendor.website, suggested: extractResult.website, actions: ['replace', 'skip'] as const },
                      { key: 'address', label: 'Address', current: vendor.address ?? null, suggested: extractResult.address, actions: ['replace', 'skip'] as const },
                      { key: 'hours_of_operation', label: 'Hours', current: vendor.hours_of_operation ?? null, suggested: extractResult.hours_of_operation ?? null, actions: ['replace', 'skip'] as const },
                      { key: 'notes', label: 'Notes', current: vendor.notes ? vendor.notes.slice(0, 60) + (vendor.notes.length > 60 ? '…' : '') : null, suggested: extractResult.notes, actions: ['replace', 'add', 'skip'] as const },
                      ...(!vendor.logo_url ? [{ key: 'logo_url', label: 'Logo', current: null as string | null, suggested: extractResult.logo_url, actions: ['replace', 'skip'] as const }] : []),
                    ]).filter(row => row.suggested !== null).map(row => (
                      <tr key={row.key} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td style={{ padding: '8px', color: 'var(--text-secondary)', fontWeight: 500 }}>{row.label}</td>
                        <td style={{ padding: '8px', color: 'var(--text-muted)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.current || <span style={{ opacity: 0.4 }}>—</span>}</td>
                        <td style={{ padding: '8px', color: 'var(--text-primary)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.suggested}</td>
                        <td style={{ padding: '8px' }}>
                          <div style={{ display: 'flex', gap: 4 }}>
                            {row.actions.map(action => (
                              <button
                                key={action}
                                type="button"
                                onClick={() => setApplyChoices(prev => ({ ...prev, [row.key]: action }))}
                                style={{
                                  fontSize: 10, padding: '2px 8px', borderRadius: 5, cursor: 'pointer', fontWeight: 500,
                                  background: applyChoices[row.key] === action ? 'rgba(139,92,246,0.2)' : 'transparent',
                                  border: applyChoices[row.key] === action ? '1px solid #8b5cf6' : '1px solid var(--border-default)',
                                  color: applyChoices[row.key] === action ? '#a78bfa' : 'var(--text-muted)',
                                }}
                              >
                                {action === 'add' ? (row.key === 'notes' ? 'Append' : 'Add to contacts') : action.charAt(0).toUpperCase() + action.slice(1)}
                              </button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {applySuccess ? (
                  <div style={{ marginTop: 20, padding: '14px 16px', borderRadius: 10, background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#34d399', fontWeight: 600, fontSize: '0.9rem' }}>
                      <span>✓</span> Changes applied successfully
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => {
                          setApplySuccess(false);
                          setExtractResult(null);
                          setExtractText('');
                          setApplyChoices({});
                        }}
                      >
                        Extract more
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm combo2"
                        style={{ background: '#7c3aed', color: 'white', border: 'none' }}
                        onClick={() => {
                          setShowAiExtract(false);
                          setApplySuccess(false);
                          setExtractResult(null);
                          setExtractText('');
                          setApplyChoices({});
                        }}
                      >
                        Done
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {applyError && (
                      <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', fontSize: '0.8125rem' }}>
                        {applyError}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                      <button type="button" className="btn btn-ghost" onClick={() => { setExtractResult(null); setApplyChoices({}); setApplyError(null); }}>Back</button>
                      <button type="button" className="btn btn-ghost combo-cancel-jello" onClick={() => setShowAiExtract(false)}>Cancel</button>
                      <button
                        type="button"
                        className="btn combo2"
                        disabled={applying}
                        onClick={applyExtracted}
                        style={{ background: '#7c3aed', color: 'white', border: 'none', opacity: applying ? 0.6 : 1 }}
                      >
                        {applying ? 'Applying…' : 'Apply Selected'}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>,
    document.body
  );
}

function PanelRow({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.875rem' }}>
      <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{icon}</span>
      {children}
    </div>
  );
}

// ─── Auto-color palette ───────────────────────────────────────────────────────
const COLOR_PALETTE = [
  '#ef4444','#f97316','#f59e0b','#eab308','#84cc16','#22c55e',
  '#10b981','#14b8a6','#06b6d4','#3b82f6','#6366f1','#8b5cf6',
  '#a855f7','#ec4899','#f43f5e','#64748b','#78716c','#0891b2',
  '#d97706','#15803d',
];

function pickNextColor(existing: string[]): string {
  for (const c of COLOR_PALETTE) {
    if (!existing.includes(c.toLowerCase())) return c;
  }
  return COLOR_PALETTE[existing.length % COLOR_PALETTE.length];
}

// ─── Manage Services Modal ────────────────────────────────────────────────────

interface ManageServicesProps {
  onClose: () => void;
  onChanged: () => void;
}

function ManageServicesModal({ onClose, onChanged }: ManageServicesProps) {
  const { data, refetch, isLoading } = useQuery({
    queryKey: ['service-categories-modal'],
    queryFn: () => api.get<ServiceCategory[]>('/vendors/service-categories'),
    staleTime: 0,
  });
  const categories: ServiceCategory[] = data?.data ?? [];

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('#6366f1');
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(() => pickNextColor([]));
  const [saving, setSaving] = useState(false);
  const mouseDownTarget = useRef<EventTarget | null>(null);

  // Update auto-color suggestion whenever categories change
  useEffect(() => {
    if (!newName) {
      setNewColor(pickNextColor(categories.map((c) => c.color.toLowerCase())));
    }
  }, [categories]); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveEdit(id: number) {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await api.put(`/vendors/service-categories/${id}`, { name: editName.trim(), color: editColor });
      await refetch();
      onChanged();
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  }

  async function deleteCategory(id: number) {
    await api.delete(`/vendors/service-categories/${id}`);
    await refetch();
    onChanged();
  }

  async function addCategory(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await api.post('/vendors/service-categories', { name: newName.trim(), color: newColor });
      await refetch();
      onChanged();
      setNewName('');
      setNewColor(pickNextColor([...categories.map((c) => c.color.toLowerCase()), newColor.toLowerCase()]));
    } finally {
      setSaving(false);
    }
  }

  return ReactDOM.createPortal(
    <div
      className="modal-overlay"
      onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
      onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) onClose(); mouseDownTarget.current = null; }}
    >
      <div className="modal" style={{ maxWidth: 520, maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: 0 }}>
        {/* Fixed Header */}
        <div style={{ flexShrink: 0, padding: '24px 28px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="modal-header" style={{ marginBottom: 0 }}>
            <h3 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tags size={18} className="modal-icon-intro" style={{ color: '#3b82f6' }} /> Manage Service Categories
            </h3>
            <button className="modal-close" onClick={onClose}><X size={18} className="btn-icon" /></button>
          </div>
        </div>

        {/* Scrollable list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 28px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {isLoading && (
            <div style={{ padding: '24px 0', textAlign: 'center' }}>
              <div className="loading-spinner" />
            </div>
          )}
          {!isLoading && categories.length === 0 && (
            <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
              No service categories yet. Add one below.
            </div>
          )}
          {categories.map((cat) => (
            <div
              key={cat.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                borderRadius: 'var(--radius-sm)', background: 'var(--bg-elevated)',
                border: '1px solid var(--border-subtle)',
              }}
            >
              {editingId === cat.id ? (
                <>
                  <input
                    type="color"
                    value={editColor}
                    onChange={(e) => setEditColor(e.target.value)}
                    style={{ width: 32, height: 32, border: '1px solid var(--border-default)', borderRadius: 6, cursor: 'pointer', padding: 2, flexShrink: 0 }}
                  />
                  <input
                    className="form-input"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    style={{ flex: 1, padding: '4px 8px', fontSize: '0.875rem' }}
                    autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(cat.id); if (e.key === 'Escape') setEditingId(null); }}
                  />
                  <button className="btn btn-primary btn-sm" disabled={saving} onClick={() => saveEdit(cat.id)}>Save</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditingId(null)}>Cancel</button>
                </>
              ) : (
                <>
                  <div style={{
                    width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                    background: cat.color, border: '1px solid rgba(0,0,0,0.15)',
                  }} />
                  <span style={{ flex: 1, fontSize: '0.875rem', fontWeight: 500 }}>{cat.name}</span>
                  {/* Preview pill */}
                  <span style={{
                    padding: '1px 8px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 500,
                    border: `1px solid ${cat.color}44`, background: `${cat.color}22`, color: cat.color, flexShrink: 0,
                  }}>{cat.name}</span>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => { setEditingId(cat.id); setEditName(cat.name); setEditColor(cat.color); }}
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ color: 'var(--accent-danger)' }}
                    onClick={() => deleteCategory(cat.id)}
                  >
                    <Trash2 size={13} />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Fixed Footer — Add new category */}
        <div style={{ flexShrink: 0, padding: '16px 28px 24px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 10 }}>
            Add New Category
          </div>
          <form onSubmit={addCategory} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <input
                type="color"
                value={newColor}
                onChange={(e) => setNewColor(e.target.value)}
                style={{ width: 36, height: 36, border: '1px solid var(--border-default)', borderRadius: 6, cursor: 'pointer', padding: 2 }}
              />
              <span style={{ fontSize: '0.6rem', color: 'var(--text-muted)' }}>auto</span>
            </div>
            <input
              className="form-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Category name…"
              style={{ flex: 1 }}
            />
            {newName.trim() && (
              <span style={{
                padding: '2px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 500, whiteSpace: 'nowrap',
                border: `1px solid ${newColor}44`, background: `${newColor}22`, color: newColor,
              }}>{newName.trim()}</span>
            )}
            <button type="submit" className="btn btn-primary combo2" disabled={saving || !newName.trim()}>
              <Plus size={15} className="btn-icon" /> Add
            </button>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Service Tag Selector (searchable multi-select) ───────────────────────────

interface ServiceTagSelectorProps {
  categories: ServiceCategory[];
  selected: string[];
  onChange: (cats: string[]) => void;
}

function ServiceTagSelector({ categories, selected, onChange }: ServiceTagSelectorProps) {
  const [search, setSearch] = useState('');
  // Root cause of "clicking a result sometimes selects a different (lower) one": `filtered` used
  // to be derived from `search` directly, so every keystroke re-filtered/re-rendered the dropdown
  // list SYNCHRONOUSLY and immediately -- a user who starts moving their mouse toward a row right
  // after their last keystroke can have that row shift to a different position (list shrinks/
  // grows, tree re-collapses) before their click lands. The click itself was always resolving
  // against whatever's actually under the cursor at that moment (native browser hit-testing on
  // plain onClick, and every row's onClick already closes over its OWN `cat`, never a stale index
  // or a separate hover-tracked "active row" state) -- so there was no wrong-item-resolved bug to
  // fix there. The list moving out from under an already-in-flight click was the real cause.
  // Fix, per the request: debounce the RENDERED list (debouncedSearch) by 150ms so it stays put
  // for the length of a normal mouse-move-then-click gesture right after typing, while the input's
  // own displayed value (`search`) still updates every keystroke with no typing lag.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 150);
    return () => clearTimeout(t);
  }, [search]);
  const [open, setOpen] = useState(false);
  const [dropdownRect, setDropdownRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const [expandedCats, setExpandedCats] = useState<Set<number>>(() => new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Build tree helpers
  const topLevel = categories.filter((c) => !c.parent_id);
  const childrenOf = (pid: number) => categories.filter((c) => c.parent_id === pid);

  const isSearching = debouncedSearch.trim().length > 0;
  const filtered = isSearching
    ? categories.filter((c) => c.name.toLowerCase().includes(debouncedSearch.toLowerCase()))
    : null;

  function openDropdown() {
    if (containerRef.current) {
      const r = containerRef.current.getBoundingClientRect();
      setDropdownRect({ top: r.bottom + 4, left: r.left, width: r.width });
    }
    setOpen(true);
  }

  // Close dropdown on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)
          && !(dropdownRef.current && dropdownRef.current.contains(e.target as Node))) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function toggle(name: string) {
    onChange(
      selected.includes(name)
        ? selected.filter((s) => s !== name)
        : [...selected, name]
    );
  }

  // Click on a category: select it AND expand its children
  function handleCatClick(cat: ServiceCategory) {
    toggle(cat.name);
    setSearch('');
    const children = childrenOf(cat.id);
    if (children.length > 0) {
      setExpandedCats((prev) => {
        const next = new Set(prev);
        next.add(cat.id);
        return next;
      });
    }
  }

  function renderCatRow(cat: ServiceCategory, depth = 0) {
    const isSelected = selected.includes(cat.name);
    const children = childrenOf(cat.id);
    const hasChildren = children.length > 0;
    const isExpanded = expandedCats.has(cat.id);
    return (
      <div key={cat.id}>
        <div
          onClick={() => handleCatClick(cat)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: `8px 12px 8px ${12 + depth * 14}px`,
            cursor: 'pointer',
            background: isSelected ? `${cat.color}18` : 'transparent',
            borderBottom: '1px solid var(--border-subtle)',
          }}
          onMouseEnter={(e) => { if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = isSelected ? `${cat.color}18` : 'transparent'; }}
        >
          <div style={{ width: 10, height: 10, borderRadius: 2, background: cat.color, flexShrink: 0 }} />
          <span style={{ fontSize: '0.875rem', flex: 1, fontWeight: depth === 0 && hasChildren ? 600 : 400 }}>{cat.name}</span>
          {isSelected && <span style={{ fontSize: '0.75rem', color: cat.color, fontWeight: 600 }}>✓</span>}
          {hasChildren && (
            <span style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
              {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </span>
          )}
        </div>
        {hasChildren && isExpanded && children.map((child) => renderCatRow(child, depth + 1))}
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {/* Selected pills + search input */}
      <div
        onClick={openDropdown}
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center',
          padding: '6px 10px', minHeight: 40,
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--bg-input, var(--bg-elevated))',
          cursor: 'text',
        }}
      >
        {selected.map((name) => {
          const cat = categories.find((c) => c.name === name);
          const color = cat?.color ?? '#6366f1';
          return (
            <span
              key={name}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '2px 6px 2px 8px', borderRadius: 999,
                fontSize: '0.75rem', fontWeight: 500,
                border: `1px solid ${color}44`,
                background: `${color}22`,
                color: color,
              }}
            >
              {name}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); toggle(name); }}
                // outline:none matches the same reset .btn already gets in global.css -- this is
                // a bare unstyled button (no .btn class, this chip's own inline-styled look), so it
                // never inherited that reset and was showing the browser's own default focus ring
                // (renders as a bright white/blue box around the whole chip in Chrome/Edge) the
                // moment a category got selected and this X button mounted/was interacted with.
                style={{ background: 'none', border: 'none', outline: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex', alignItems: 'center' }}
              >
                <X size={10} />
              </button>
            </span>
          );
        })}
        <input
          value={search}
          onChange={(e) => { setSearch(e.target.value); openDropdown(); }}
          onFocus={openDropdown}
          placeholder={selected.length === 0 ? 'Search and select services…' : ''}
          style={{
            border: 'none', outline: 'none', background: 'transparent',
            fontSize: '0.8125rem', color: 'var(--text-primary)',
            minWidth: 140, flex: 1,
          }}
        />
        <ChevronDown size={14} color="var(--text-muted)" style={{ flexShrink: 0 }} />
      </div>

      {/* Dropdown — portal to body to escape modal backdrop-filter containing block */}
      {open && dropdownRect && ReactDOM.createPortal(
        <div ref={dropdownRef} style={{
          position: 'fixed', top: dropdownRect.top, left: dropdownRect.left, width: dropdownRect.width, zIndex: 10000,
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-md)',
          overflow: 'hidden',
        }}>
        <div style={{ background: 'var(--bg-elevated)', maxHeight: 240, overflowY: 'auto' }}>
          {isSearching ? (
            filtered!.length === 0 ? (
              <div style={{ padding: '10px 14px', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                No categories found
              </div>
            ) : (
              filtered!.map((cat) => renderCatRow(cat))
            )
          ) : topLevel.length === 0 ? (
            <div style={{ padding: '10px 14px', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
              No categories found
            </div>
          ) : (
            topLevel.map((cat) => renderCatRow(cat))
          )}
        </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function CheckboxField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: '0.8125rem',
        color: 'var(--text-secondary)',
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ accentColor: 'var(--accent-primary)' }}
      />
      {label}
    </label>
  );
}
